// useChatShell — SHARED chat-shell state/logic hook (platform-isolation Phase 1, #1657).
// Extracted VERBATIM from the body of components/App.tsx (lines 113-2110). Holds all
// session/socket/tab/handler state + effects; returns the identifiers consumed by the
// App-level page-route early-returns and the shell renderer (mobile/MobileShell).
// FIREWALL: lives in hooks/ (shared) — MUST NOT import mobile/** or desktop/**.
// Behavior-preserving refactor: same logic, same execution order. Event listeners moved
// verbatim, in place; event-bus re-homing is Phase 1a (separate PR).

import { useState, useEffect, useCallback, useRef, useMemo } from 'preact/hooks';
import { onAuthChange } from '../auth/google-auth';
import { useSocket } from './useSocket';
import {
  useUiActiveView,
  setActiveView,
  bootRestoreFileDrawer,
} from '../state/uiStateStore';
import { useTtsShared } from './useTtsContext';
import { useSessionSound } from './useSessionSound';
import { useRoles } from './useRoles';
import { useAttachments } from './useAttachments';
import { type ViewportMessageFocus } from '../components/ChatArea';
import { setActAsPersona } from '../api/admin';
import { useProjectTags } from './useProjectTags';
import type { SshTerminalHandle } from '../components/SshTerminal';
import { stripAnsi } from '../utils/stripAnsi';
import { reconstructWrappedUrls } from '../utils/sshUrlReconstruct';
import { openDrawer, closeDrawer, openForCascadeGate, useDrawerPanel, getDrawerPanel, useDrawerStore, type DrawerPanel } from '../state/drawerStore';
import { useSettingsStore } from '../state/settings.store';
import { useComposerStore, getSessionRegion, useIsBusy } from '../state/composerStore';
import { useDocTtsStore } from '../state/docTtsStore';
import { isMicRecording } from '../state/micAudioCoordination';
import { useAgentListStore, getVisibleDots } from '../state/agentListStore';
import {
  offer as coachOffer,
  show as coachShow,
  dismiss as coachDismiss,
  evaluate as coachEvaluate,
  arm as coachArm,
  markDiscovered as coachMarkDiscovered,
  resetFirstRunCoaching,
  useCoachStore,
  coachAnchorSelector,
  type CoachSnapshot,
} from '../state/coachStore';

// Currently-mounted element-anchored coach targets, as the EXACT selector strings the
// catalog uses — routed through coachAnchorSelector so the snapshot and the catalog
// can't drift (SE-16). evaluate()'s anchor gate is exact string equality, so a
// `[data-coach-id=fab]` element becomes `[data-coach-id=fab]` here, never quoted.
function deriveMountedAnchors(): string[] {
  if (typeof document === 'undefined') return [];
  const ids = Array.from(document.querySelectorAll('[data-coach-id]'))
    .map((el) => el.getAttribute('data-coach-id'))
    .filter((id): id is string => !!id);
  return Array.from(new Set(ids)).map(coachAnchorSelector);
}
import { useIsDesktop, isDesktopShellNow } from '../shared-ui/useIsDesktop';
import { type SharePayload } from '../components/ShareTargetModal';
import { useModels, type EffortLevel } from './useModels';
import { usePullToRefresh } from './usePullToRefresh';
import { useSwipeSession } from './useSwipeSession';
import { useDoubleTap } from './useDoubleTap';
import { haptic } from '../utils/haptic';
import { readInitialBratoTheme } from '../themeSync';
import { getBrand, getBrandId } from '../brands';
import { getSettings, getProfile, checkQuota, incrementQuota } from '../api/settings';
import { WsEvent } from '@voice-assistant/shared';
import { buildExplainText } from '../shared-ui/QuotePill';
import { type ExplainPayload } from '../components/ExplainTargetModal';
import { buildSessionHash, parseSessionHash } from '../utils/sessionUrl';
import { nextSessionIndex } from '../utils/carousel';
import { useChatFind } from './useChatFind';
import { initWasm } from '../wasm/bridge';

// Pre-seed currentSessionId from URL on deep links (#s/<id>) before React/useSocket
// initialize — the hook reads localStorage on first render, so we must write before that.
// Also capture the effective initial session ID for the mount effect below
// (avoids a forward reference to the `currentSessionId` returned by useSocket).
const _initialSessionId: string | null = (() => {
  const hash = window.location.hash;
  if (hash.startsWith('#s/')) {
    const id = hash.slice(3);
    if (id) {
      try { localStorage.setItem('voice-assistant-current-session-id', id); } catch {}
      return id;
    }
  }
  try { return localStorage.getItem('voice-assistant-current-session-id'); } catch { return null; }
})();

// Computed once at module scope (before React) — used in mount effect and popstate handler.
export const isStandaloneMode: boolean =
  window.matchMedia('(display-mode: standalone)').matches ||
  (window.navigator as unknown as { standalone?: boolean }).standalone === true;

// Session-hash ownership is scoped to the chat route ('/'). App.tsx calls useChatShell()
// UNCONDITIONALLY before its pathname early-returns (/arch, /admin, /guide, ...), so this hook's
// effects mount even on those other full-page routes. Without this guard, the mount effect below
// would stomp e.g. /arch's own `#arch/<level>/<id>` deep-link hash with `#s/<id>`/`#chat` on every
// load (confirmed empirically: cold-loading /arch produced `/arch#s/<lastSessionId>`), and the
// popstate handler would react to hash changes that belong to another page's own scheme. Module
// scope like isStandaloneMode above: these are full-page-reload routes (no client-side pathname
// routing), so pathname is stable for the component's lifetime — a fresh navigation to '/' always
// re-evaluates this module fresh.
const isChatRoute: boolean = window.location.pathname === '/';

export function useChatShell() {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [userInitial, setUserInitial] = useState('?');
  /** WASM enabled — true after initWasm() succeeds (requires ?wasm=1 or localStorage flag) */
  const [wasmActive, setWasmActive] = useState(false);
  /** Master TTS switch — sourced from settings store (memoSettings.autoPlay).
   *  settings-zustand-migration Phase 4: replaced the local useState +
   *  localStorage('voice-assistant-autospeak') mirror with a direct store
   *  subscription. Boot-time default before loadFromBackend resolves is
   *  DEFAULT_USER_PREFERENCES.autoPlay (true). The legacy localStorage key is
   *  no longer read OR written; cleanup of the orphan key is a follow-up. */
  const autoSpeak = useSettingsStore((s) => s.autoPlay ?? true);
  const { isSessionSoundOn, toggleSessionSound } = useSessionSound();
  // Console view hidden from normal UI — only used in SSH isSelf mode
  const [consoleView, setConsoleView] = useState(false);
  const { speaking, speakingSourceKey, speak, stop, feedChunk, flush, voices, selectedVoiceId, selectedVoiceName, setVoice, previewVoice, speed: ttsSpeed, cycleSpeed } = useTtsShared();
  const { isAdmin, isAdminContext, userEmail } = useRoles();
  const { tags, checkedProjects, toggleProject, checkProject, clearAll: clearAllTags, getCheckedKeys } = useProjectTags();
  const {
    attachments,
    hasAttachments,
    processing: attachProcessing,
    error: attachError,
    triggerFilePicker,
    removeAttachment,
    clearAttachments,
    fileInputProps,
  } = useAttachments();

  const { pulling, pullDistance, refreshing } = usePullToRefresh();
  const { models: registryModels } = useModels();
  // Derive the best Opus model from registry — no hardcoded model IDs
  const opusModelId = useMemo(
    () => registryModels.find(m => m.provider === 'anthropic' && m.tier === 'powerful')?.modelId ?? 'claude-opus-4-8',
    [registryModels],
  );

  const [sttLang, setSttLang] = useState<string>('');
  const [activeRole, setActiveRole] = useState<string | null>(null);
  const activeRoleRef = useRef<string | null>(null);
  useEffect(() => { activeRoleRef.current = activeRole; }, [activeRole]);
  const [tempDocsDirName, setTempDocsDirName] = useState<string>('');
  const [plansDirName, setPlansDirName] = useState<string>('');
  const [projectDocsDirName, setProjectDocsDirName] = useState<string>('');
  const [registryDirName, setRegistryDirName] = useState<string>('');
  const [archiveDirName, setArchiveDirName] = useState<string>('');
  // Files drawer open-state is owned by drawerStore (panel==='files'); FileListDrawer
  // still uses fileDrawerStore internally for its refresh token.

  // Active view is store-owned Class-B state (uiStateStore, `va-ui-state-v1`) —
  // Phase 1 of user-ui-preferences-state (#1566). The hook reads via selector and
  // writes via the named action (R-A2); rehydration happens synchronously at
  // module init, so the FIRST render already sees the restored view (no home-flash).
  const activeView = useUiActiveView();
  // Ref to track current view without stale closure in popstate handler
  const activeViewRef = useRef<'list' | 'chat'>('list');
  useEffect(() => { activeViewRef.current = activeView; }, [activeView]);

  // Refs to track modal open state without stale closures in popstate handler
  const logbookOpenRef = useRef(false);
  // Settings + Files open-state are owned by drawerStore (panel==='settings'|'files') —
  // no booleans/refs. The popstate handler reads it non-reactively via
  // getDrawerPanel(getState()); the render reads it reactively via this selector.
  const drawerPanel = useDrawerPanel();
  const [logbookOpen, setLogbookOpen] = useState(false);
  useEffect(() => { logbookOpenRef.current = logbookOpen; }, [logbookOpen]);
  // Logbook persisted state — survives modal close/reopen so user can navigate away and come back
  const [logbookText, setLogbookText] = useState('');
  const [logbookScreenshot, setLogbookScreenshot] = useState<string | null>(null);
  const clearLogbookState = useCallback(() => { setLogbookText(''); setLogbookScreenshot(null); }, []);

  const [keyboardStripOpen, setKeyboardStripOpen] = useState(false);
  const chatFind = useChatFind();
  const [maxOpenTabs, setMaxOpenTabs] = useState(5);
  const [modelSheetOpen, setModelSheetOpen] = useState(false);
  /** Pending high-cost send awaiting user confirmation. text=null when nothing pending. */
  const [pendingHighCostSend, setPendingHighCostSend] = useState<{ effort: EffortLevel; send: () => void } | null>(null);
  const [interAgentSharingEnabled, setInterAgentSharingEnabled] = useState(true);

  // PWA install prompt (Chrome/Android only; iOS doesn't fire beforeinstallprompt).
  // Dismiss is sticky for 30 days via `pwa-install-dismissed-at` localStorage key
  // — without it, the pill would re-appear on every reload (Chrome re-fires
  // beforeinstallprompt) and fight with the `+` FAB at the bottom of the list.
  const [installDismissed, setInstallDismissed] = useState(() => {
    try {
      const ts = parseInt(localStorage.getItem('pwa-install-dismissed-at') || '0', 10);
      const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
      return ts > 0 && Date.now() - ts < THIRTY_DAYS_MS;
    } catch { return false; }
  });
  const [installPromptAvailable, setInstallPromptAvailable] = useState(false);
  const installPromptRef = useRef<{ prompt: () => void; userChoice: Promise<{ outcome: string }> } | null>(null);
  const dismissInstallPill = useCallback(() => {
    try { localStorage.setItem('pwa-install-dismissed-at', String(Date.now())); } catch {}
    setInstallDismissed(true);
  }, []);

  // PWA standalone back-exit guard ("Press back again to exit" toast — coach.show('back-exit'))
  const backExitPendingRef = useRef(false);

  // Swipe-to-switch-session — callback ref so the effect re-fires when the element mounts
  // (useRef stays null during the loading screen; callback ref triggers a state change)
  const [chatPanelEl, setChatPanelEl] = useState<HTMLDivElement | null>(null);
  const chatSlideRef      = useRef<HTMLDivElement>(null);
  const leftShadowRef     = useRef<HTMLDivElement>(null);
  const rightShadowRef    = useRef<HTMLDivElement>(null);
  const leftSliverRef     = useRef<HTMLDivElement>(null);
  const rightSliverRef    = useRef<HTMLDivElement>(null);

  // Session badge data for the swipe sliver (prev/next session avatar preview)
  // Updated on openTabs/currentSessionId/sessions changes; read during touchmove.
  const prevNextBadgeRef = useRef<{
    prev: { letter: string; color: string } | null;
    next: { letter: string; color: string } | null;
  }>({ prev: null, next: null });

  const SWIPE_THRESHOLD_FOR_SHADOW = 80; // must match useSwipeSession.ts

  /** Apply translateX to the chat content slide wrapper — direct DOM for perf.
   *  Also drives the edge shadow and adjacent-session sliver at 60fps. */
  const applySlide = useCallback((offset: number, animated: boolean) => {
    const el = chatSlideRef.current;
    if (!el) return;
    el.style.transition = animated ? 'transform 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none';
    el.style.transform  = offset === 0 ? '' : `translateX(${offset}px)`;

    const abs = Math.abs(offset);
    const progress = Math.min(abs / SWIPE_THRESHOLD_FOR_SHADOW, 1);

    if (animated || offset === 0) {
      // Reset all overlays
      for (const ref of [leftShadowRef, rightShadowRef, leftSliverRef, rightSliverRef]) {
        if (ref.current) ref.current.style.opacity = '0';
      }
      return;
    }

    // offset > 0 → dragging right → prev session peeks from LEFT
    const [activeShadow, inactiveShadow] = offset > 0
      ? [leftShadowRef.current,  rightShadowRef.current]
      : [rightShadowRef.current, leftShadowRef.current];
    const [activeSliver, inactiveSliver] = offset > 0
      ? [leftSliverRef.current,  rightSliverRef.current]
      : [rightSliverRef.current, leftSliverRef.current];

    // Shadow: fades in when drag > 30% of threshold
    if (activeShadow) activeShadow.style.opacity = progress > 0.3 ? String(Math.min((progress - 0.3) / 0.7, 1)) : '0';
    if (inactiveShadow) inactiveShadow.style.opacity = '0';

    // Sliver: fades in when drag > 10% of threshold; update badge
    const badgeData = offset > 0 ? prevNextBadgeRef.current.prev : prevNextBadgeRef.current.next;
    if (activeSliver) {
      if (badgeData && progress > 0.1) {
        activeSliver.style.opacity = String(Math.min((progress - 0.1) / 0.4, 1));
        const badgeEl = activeSliver.querySelector<HTMLElement>('.swipe-sliver-badge');
        if (badgeEl) {
          badgeEl.textContent = badgeData.letter;
          badgeEl.style.background = badgeData.color;
        }
      } else {
        activeSliver.style.opacity = '0';
      }
    }
    if (inactiveSliver) inactiveSliver.style.opacity = '0';
  }, []);

  // History-backed navigation helpers for correct Android/Huawei edge-swipe-back UX.
  //
  // Android system back gesture ONLY recognises history entries with a different URL.
  // Same-URL pushState entries are invisible to the gesture — the OS jumps past them
  // and fires browser back to the previous real page.
  //
  // URL scheme (Phase 2 — agents-drawer-carousel-reorg.md 2c: chat IS home, the list
  // is the Agents drawer, NOT a route):
  //   session  → voice.akrsmv.net/#s/<sessionId> { vaView: 'chat', sessionId }     (pushState on enter)
  //   legacy   → voice.akrsmv.net/#chat          { vaView: 'chat', sessionId:null } (new-session / zero-session placeholder)
  //   file     → voice.akrsmv.net/#file:...      { codeViewer: true }              (CodeViewer, unchanged)
  //
  // The bottom of the history stack is the LAST-ACTIVE SESSION's chat (not a list view).
  // A bare `/` (no hash) resolves to that session (or the zero-session empty-state).
  // Establish that chat anchor on first load. Handle #s/<id> deep links and legacy #chat.
  useEffect(() => {
    if (!isChatRoute) return; // this route owns its own hash scheme (or none) — see isChatRoute above
    const sessionIdFromHash = parseSessionHash(window.location.hash);
    // _initialSessionId is captured at module level (the persisted last-active session)
    // to avoid forward-referencing the currentSessionId returned by useSocket below.
    const home = sessionIdFromHash || _initialSessionId;
    const homeHash = home ? buildSessionHash(home) : '#chat';
    const homeState = { vaView: 'chat', sessionId: home ?? null };

    // In standalone PWA mode, push an exit-guard entry BELOW the home session so we can
    // intercept the first back-press and show a "press again to exit" confirmation.
    // The bottom-of-stack anchor is the home session's chat, not a list view.
    if (isStandaloneMode) {
      const { pathname, search } = window.location;
      window.history.replaceState({ exitGuard: true }, '', pathname + search);
      window.history.pushState(homeState, '', homeHash);
    } else {
      window.history.replaceState(homeState, '', homeHash);
    }

    // Chat is the only view now; always anchor there. A deep link (#s/<id>) overrides
    // the persisted view; otherwise the persisted last-active session still lands in chat.
    setActiveView('chat');

    // Non-destructive reload (#1566 Phase 1): if the unified file drawer was open
    // when the page unloaded, re-open it ON TOP of the (deep-link-resolved) home
    // chat anchored above — the #s/<id> deep link keeps priority for WHICH session
    // renders underneath; the drawer is orthogonal overlay state. The pushed
    // `modal: 'files'` entry mirrors openDrawerH's state shape so the Back gesture
    // pops it and closes the drawer, leaving the exit-guard stack intact — but it
    // deliberately KEEPS the current URL (no '#files' rewrite): useSocket's connect
    // handler re-parses location.hash at connect time (resolveBootSessionId), so
    // rewriting the hash here would clobber a #s/<id> deep link and boot the WRONG
    // session (caught live on dev-voice during Phase-1 verification). Mobile shell
    // only — the desktop shell does not render DrawerHost, and a phantom open
    // drawer would eat one Back press there (popstate priority 1).
    if (!isDesktopShellNow()) {
      bootRestoreFileDrawer(() => {
        window.history.pushState({ vaView: 'chat', modal: 'files' }, '', window.location.href);
      });
    }
  }, []); // runs once on mount — currentSessionId captured from initial localStorage load

  // Initialise WASM module on mount (opt-in via ?wasm=1 or localStorage flag)
  useEffect(() => {
    initWasm().then((ok) => { if (ok) setWasmActive(true); });
  }, []);

  const goToChat = useCallback((sessionId?: string) => {
    // Chat is home: if we're already in chat (the only view), still sync the URL hash
    // to the target session so deep-link / reload / Back restore the right chat. When
    // arriving from a non-chat surface (none exist post-Phase-2, but the guard is
    // harmless) push a fresh entry instead.
    const hash = sessionId ? buildSessionHash(sessionId) : '#chat';
    const stateObj = { vaView: 'chat', sessionId: sessionId ?? null };
    if (activeViewRef.current !== 'chat') {
      window.history.pushState(stateObj, '', hash);
    } else if (window.location.hash !== hash && !window.location.hash.startsWith('#file:')) {
      // Already in chat but the hash points elsewhere — replace it in place so it
      // matches the target chat. Most importantly this overwrites the dangling
      // `#sessions` (or other drawer) hash left after a drawer session-select /
      // new-session, which would otherwise re-open the drawer on reload (the Phase-2
      // stale-hash fix). replaceState fires NO popstate, so there is no race with the
      // drawer-close handler. The #file: CodeViewer hash is owned by CodeViewer — skip it.
      window.history.replaceState(stateObj, '', hash);
    }
    setActiveView('chat');
  }, []);

  // Placeholder refs for the early effects (cascade-gated, popstate) — synced after
  // useSocket / openDrawerH are defined below. Using refs lets these handlers stay here
  // (before useSocket) without TDZ, AND keeps lexical order clean for eslint
  // no-use-before-define (declare the refs above their first use).
  const openTabsRef = useRef<string[]>([]);
  const switchTabRef = useRef<(sessionId: string) => void>(() => {});
  // The home session for back-stack re-anchoring (standalone exit-guard), synced from
  // currentSessionId ?? openTabs[0] below. null → zero-session empty-state.
  const currentSessionIdHomeRef = useRef<string | null>(_initialSessionId);
  // Always-fresh current session id for prefill producers (platform-isolation #1657 Phase
  // 2b). The old `prefill-input` CustomEvent late-bound to InputBar's current session; the
  // direct composerStore.appendOrReplaceDraft() call needs that same fresh target — read
  // inside any setTimeout so a session switch between schedule + fire targets correctly.
  const currentSessionIdRef = useRef<string | null>(_initialSessionId);

  // Handle Android/Huawei edge-swipe-back and hardware back button.
  //
  // History stack (Phase 2 — chat is home; URLs differ → OS back gesture works on all levels):
  //   [exitGuard?] [/#s/<home> {chat}] → [/#s/<id2> {chat}] → ... → [/#modal {modal}]
  //
  // Modals push their own hash URLs when opened. Popping a modal entry closes it.
  // CodeViewer pushes its own { codeViewer: true } entry at #file:... URL.
  // Navigating back to a { vaView: 'chat', sessionId } entry restores that session.
  // Drift: if sessionId no longer exists in openTabs, switch to a surviving tab (or
  // the zero-session empty-state) — there is NO list view to fall back to.
  useEffect(() => {
    if (!isChatRoute) return; // this route owns its own hash/popstate scheme (or none) — see isChatRoute above
    const onPop = (_e: PopStateEvent) => {
      // Priority 1: close any open modal overlay (modals sit on top of everything).
      // Any open drawerStore drawer (sessions/usage/tasks/ops/settings/files) closes on
      // Back — each was opened via openDrawerH, which pushed a matching history entry.
      if (getDrawerPanel(useDrawerStore.getState())) { closeDrawer(); return; }
      if (logbookOpenRef.current) { setLogbookOpen(false); logbookOpenRef.current = false; return; }

      // Priority 2: CodeViewer handles its own popstate
      const st = window.history.state;
      if (st?.codeViewer) return;

      // Priority 2.5: standalone exit-guard — first back from the home session shows the
      // toast; second exits. Re-anchor on the home session's chat (not a list view).
      if (st?.exitGuard) {
        if (isStandaloneMode) {
          if (backExitPendingRef.current) {
            // Second back within 2s → allow exit (don't re-push)
            return;
          }
          // Re-push the home session's chat entry above the guard so the user stays in
          // their last-active chat (chat is home). currentSessionId is the live anchor;
          // fall back to the first open tab, then the legacy #chat placeholder.
          const home = currentSessionIdHomeRef.current;
          const homeHash = home ? buildSessionHash(home) : '#chat';
          window.history.pushState({ vaView: 'chat', sessionId: home ?? null }, '', homeHash);
          setActiveView('chat');
          coachShow({ id: 'back-exit', kind: 'toast', body: 'Press back again to exit', ttlMs: 2000, dismissible: false });
          backExitPendingRef.current = true;
          setTimeout(() => { backExitPendingRef.current = false; }, 2000);
        }
        return;
      }

      if (st?.vaView === 'chat') {
        // Navigated back to a per-session history entry.
        const sid = st.sessionId as string | null | undefined;
        if (sid && openTabsRef.current.includes(sid)) {
          // Restore the session this entry belongs to.
          switchTabRef.current(sid);
          if (activeViewRef.current !== 'chat') {
            setActiveView('chat');
          }
        } else if (sid) {
          // Drift: this session was deleted. There is no list view to fall back to —
          // switch to a surviving open tab (or leave the zero-session empty-state to
          // render) and replace the bad entry with that session's hash (or #chat).
          const survivor = openTabsRef.current[0] ?? null;
          if (survivor) switchTabRef.current(survivor);
          const hash = survivor ? buildSessionHash(survivor) : '#chat';
          window.history.replaceState({ vaView: 'chat', sessionId: survivor }, '', hash);
          setActiveView('chat');
        }
        // No sessionId (legacy #chat entry) — stay in chat, no session switch needed.
        return;
      }

      // No special entry — chat is the only view; stay there.
      setActiveView('chat');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []); // intentionally empty — uses refs to avoid stale closures

  // Generic history-aware open/close for EVERY drawerStore drawer (unified-drawer
  // P3 polish). Open pushes a per-panel history entry (modal: panel, #panel) so the
  // Back gesture closes the drawer; the popstate handler above closes whatever drawer
  // is open. Close pops that entry (→ popstate → closeDrawer) so there's no stale
  // entry left behind. All drawer triggers funnel through these so Back is consistent
  // for usage/tasks/ops/settings/files alike.
  const openDrawerH = useCallback((panel: DrawerPanel) => {
    window.history.pushState({ vaView: activeViewRef.current, modal: panel }, '', '#' + panel);
    openDrawer(panel);
  }, []);
  const closeDrawerH = useCallback(() => {
    const panel = getDrawerPanel(useDrawerStore.getState());
    if (panel && window.history.state?.modal === panel) window.history.back();
    else closeDrawer();
  }, []);

  // Files drawer open/close — thin history-aware wrappers (names kept for callers).
  const openFiles = useCallback(() => openDrawerH('files'), [openDrawerH]);
  const closeFiles = useCallback(() => closeDrawerH(), [closeDrawerH]);

  // Close keyboard strip whenever the action cluster collapses
  useEffect(() => {
    const handler = () => setKeyboardStripOpen(false);
    document.addEventListener('fab-cluster-collapsed', handler);
    return () => document.removeEventListener('fab-cluster-collapsed', handler);
  }, []);

  /** Open browser in a new tab (token-authenticated if possible, plain URL fallback) */
  const openBrowser = async () => {
    try {
      const { authFetch } = await import('../api/authFetch');
      const res = await authFetch('/api/me/browser-token');
      const data = await res.json();
      if (data.url) { window.open(data.url, '_blank'); return; }
    } catch { /* fallback below */ }
    window.open('https://browser.akrsmv.net', '_blank');
  };

  /* ── iframe-based browser panel (disabled — Google blocks OAuth in iframes)
   * Kept for future revisit with Firefox/non-Chromium browsers.
   * To re-enable: restore 'browser' to activeView union type,
   * add browserLoaded/browserUrl state, and the Panel 3 JSX block below.
   *
   * const [browserLoaded, setBrowserLoaded] = useState(false);
   * const [browserUrl, setBrowserUrl] = useState<string | null>(null);
   * const openBrowserEmbed = async () => { ... };
   */
  const [chatTab, setChatTab] = useState<'chat' | 'details'>('chat');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<'sessions' | 'commands' | 'settings'>('sessions');
  const [drawerCommands, setDrawerCommands] = useState<{name: string; description: string}[]>([]);
  const [theme, setTheme] = useState<string | null>(() => readInitialBratoTheme());

  const handleThemeSwitch = useCallback((letter: string) => {
    const next = letter === 'r' ? null : (theme === letter ? null : letter);
    setTheme(next);
    // settings-zustand-migration Phase 2: store.setTheme handles both the
    // canonical persist write AND the `brato-theme` write-through (used by
    // themeSync.ts's pre-render boot path). Legacy `valko-theme` removal stays
    // here — it's a one-shot pre-rebrand cleanup, not a hot-path write.
    useSettingsStore.getState().setTheme(next);
    try { localStorage.removeItem('valko-theme'); } catch { /* ignore */ }
  }, [theme]);

  useEffect(() => {
    if (theme) document.documentElement.setAttribute('data-theme', theme);
    else document.documentElement.removeAttribute('data-theme');
  }, [theme]);

  useEffect(() => {
    fetch('/api/commands')
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setDrawerCommands(data))
      .catch(() => {});
  }, [authenticated]);
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const openSettings = useCallback(() => openDrawerH('settings'), [openDrawerH]);
  const closeSettings = useCallback(() => closeDrawerH(), [closeDrawerH]);

  const [consentNeeded, setConsentNeeded] = useState(false);
  const [geoNeeded, setGeoNeeded] = useState(false);
  const [browserConsentPrompt, setBrowserConsentPrompt] = useState<{ sessionId: string; version: string } | null>(null);
  const [browserTierGatePrompt, setBrowserTierGatePrompt] = useState<{ sessionId: string; reason: string } | null>(null);
  const [explainPayload, setExplainPayload] = useState<ExplainPayload | null>(null);
  const [onboardingNeeded, setOnboardingNeeded] = useState(false);
  const [userProvisioningStatus, setUserProvisioningStatus] = useState<string>('none');
  const [preferWhisper, setPreferWhisper] = useState(false);
  // Phase 4: autoSubmitVoice sourced from store (direct-column setting).
  const autoSubmitVoice = useSettingsStore((s) => s.autoSubmitVoice === true);
  // Phase 4: sttLanguage sourced from store (local-only setting). Previously
  // lived in useSocket as both a useRef + useState; that exposure was removed
  // in this phase because language is a setting, not a socket concern.
  const sttLanguage = useSettingsStore((s) => s.sttLanguage);
  const [onboardingGithubConnected, setOnboardingGithubConnected] = useState(false);
  const [onboardingGithubLogin, setOnboardingGithubLogin] = useState<string | undefined>(undefined);
  const [onboardingGithubOauthEnabled, setOnboardingGithubOauthEnabled] = useState(false);
  const [memoSaving, setMemoSaving] = useState(false);
  const [memosEnabled, setMemosEnabled] = useState(true); // default true until profile loads
  const [lessonGeneratorEnabled, setLessonGeneratorEnabled] = useState(false); // default false until profile loads
  const [readerModeEnabled, setReaderModeEnabled] = useState(true); // default true until profile loads (Brato-only; off elsewhere)
  const [logbookEnabled, setLogbookEnabled] = useState(true); // default true until profile loads (Brato-only; off elsewhere)
  const [browserPanelEnabled, setBrowserPanelEnabled] = useState(true); // default true until profile loads
  const [pulseEnabled, setPulseEnabled] = useState(true); // default true until profile loads
  const [guideEnabled, setGuideEnabled] = useState(true); // default true until profile loads; backend guide_enabled gates this
  const [modelSelectorEnabled, setModelSelectorEnabled] = useState(false); // default false until profile loads (in-session picker gated off on all profiles, #1071)
  const [geminiEnabled, setGeminiEnabled] = useState(true); // default true until profile loads (off on protofac*/valko*)
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [billingEnabled, setBillingEnabled] = useState(false); // default false until profile loads
  const [contextualDrawerMigration, setContextualDrawerMigration] = useState(false); // default false until profile loads
  const [sshTerminalEnabled, setSshTerminalEnabled] = useState(false); // default false until profile loads
  const [sandboxFsEnabled, setSandboxFsEnabled] = useState(false); // default false until profile loads (protofac*, SE-PF6 #1391)
  const [opsPanelEnabled, setOpsPanelEnabled] = useState(false); // default false until profile loads
  const [devPreviewEnabled, setDevPreviewEnabled] = useState(false); // default false until profile loads (dev-preview HMR button #1403; admin + prod-only)
  const [workflowVizEnabled, setWorkflowVizEnabled] = useState(false); // default false until profile loads (Agent Runs; admin-only — the backend gate is the real one)
  // Feature-surface tabs (SE-PF4). Default true = full surface until profile loads (matches the
  // pre-profile behavior where dev tabs always render); Protofac flips roads off via /profile.
  const [tasksTabEnabled, setTasksTabEnabled] = useState(true);
  const [plansTabEnabled, setPlansTabEnabled] = useState(true);
  const [roadsTabEnabled, setRoadsTabEnabled] = useState(true);
  const [usagePanelEnabled, setUsagePanelEnabled] = useState(true);
  // Bottom-nav r7: Usage drawer (opened from the BottomNav leading slot, replaces the
  // old context slot). Mounts <UsageTab/> in a themed bottom sheet.
  // unified-drawer P3: the usage drawer's open-state is now owned by drawerStore and
  // rendered through DrawerHost (openDrawer('usage')) — no per-drawer boolean / swipe
  // hook lives here anymore. usagePanelEnabled stays (it gates the panel + the trigger).
  const [projectChipsEnabled, setProjectChipsEnabled] = useState(false); // default false until profile loads
  const [viewportFocus, setViewportFocus] = useState<ViewportMessageFocus | null>(null);
  const [chatAtBottom, setChatAtBottom] = useState(true);
  const [kebabOpen, setKebabOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetSaveKnowledge, setResetSaveKnowledge] = useState(true);
  const [resetPendingModel, setResetPendingModel] = useState<string | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteSaveKnowledge, setDeleteSaveKnowledge] = useState(true);
  const [deleteTargetIds, setDeleteTargetIds] = useState<string[]>([]);
  const kebabRef = useRef<HTMLDivElement>(null);

  // Close gear menu on outside click
  useEffect(() => {
    if (!kebabOpen) return;
    const handler = (e: MouseEvent) => {
      if (kebabRef.current && !kebabRef.current.contains(e.target as Node)) setKebabOpen(false);
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, [kebabOpen]);

  const handleResumeFailed = useCallback(() => {
    coachShow({ id: 'resume', kind: 'toast', body: 'Previous session expired, started fresh', ttlMs: 4000 });
  }, []);

  // Model catalog update toast
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const parts: string[] = [];
      if (detail?.newModels?.length) {
        const names = detail.newModels.map((m: any) => m.displayName).join(', ');
        parts.push(`New: ${names}`);
      }
      if (detail?.retiredModels?.length) {
        const names = detail.retiredModels.map((m: any) => m.displayName).join(', ');
        parts.push(`Retired: ${names}`);
      }
      if (parts.length) {
        coachShow({ id: 'model-switched', kind: 'toast', body: parts.join(' · '), ttlMs: 6000 });
      }
    };
    window.addEventListener('model-catalog-updated', handler);
    return () => window.removeEventListener('model-catalog-updated', handler);
  }, []);

  // ── Consult model picker ─────────────────────────────────────────────
  const CONSULT_PROVIDERS = [
    { id: 'opus' as const,   icon: '🧠', label: 'Opus',   color: 'rgba(120, 100, 220, 0.85)' },
    { id: 'gemini' as const, icon: '🔮', label: 'Gemini', color: 'rgba(40, 160, 80, 0.85)'  },
    { id: 'haiku' as const,  icon: '⚡', label: 'Haiku',  color: 'rgba(60, 130, 200, 0.85)' },
    { id: 'ollama' as const, icon: '🦙', label: 'Ollama', color: 'rgba(200, 120, 60, 0.85)' },
  ] as const;
  type ConsultTarget = typeof CONSULT_PROVIDERS[number]['id'];

  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const [consultTarget, setConsultTarget] = useState<ConsultTarget>('opus');

  // ── Opus / Share selection mode ──────────────────────────────────────
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectionPurpose, setSelectionPurpose] = useState<'opus' | 'share'>('opus');
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(new Set());
  const [opusPrompt, setOpusPrompt] = useState('');
  const [opusSuggestionSource, setOpusSuggestionSource] = useState<'classifier' | 'manual' | null>(null);
  // Buffered classifier suggestion — applied once messages are available
  const [pendingOpusSuggestion, setPendingOpusSuggestion] = useState<{ prompt: string; source: 'classifier' } | null>(null);
  const [shareTargetOpen, setShareTargetOpen] = useState(false);
  const [slashPopover, setSlashPopover] = useState<{ cmd: string; x: number; y: number } | null>(null);
  /** Text staged for injection into the target session's draft (via composerStore.appendOrReplaceDraft) */
  const pendingShareTextRef = useRef<string | null>(null);
  /** Session ID we're switching to for share injection ('new' = waiting for new session) */
  const pendingShareTargetRef = useRef<string | 'new' | null>(null);
  /** If true, auto-submit the prefilled text without requiring the user to press Send */
  const pendingAutoSubmitRef = useRef<boolean>(false);
  /** Typed payload for unified share modal — controls header, sections, format options */
  const pendingSharePayloadRef = useRef<SharePayload | null>(null);

  const enterSelectionMode = useCallback((prefill: string, source: 'classifier' | 'manual', msgs: { id?: string; role: string }[], target: ConsultTarget = 'opus') => {
    setSelectionMode(true);
    setSelectionPurpose('opus');
    setConsultTarget(target);
    setOpusPrompt(prefill);
    setOpusSuggestionSource(source);
    // Pre-select last 5 user+assistant messages
    const eligible = msgs.filter(m => m.role === 'user' || m.role === 'assistant').slice(-5);
    setSelectedMessageIds(new Set(eligible.map(m => m.id).filter(Boolean) as string[]));
  }, []);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectionPurpose('opus');
    setSelectedMessageIds(new Set());
    setOpusPrompt('');
    setOpusSuggestionSource(null);
    setPendingOpusSuggestion(null);
  }, []);

  const toggleMessageSelection = useCallback((id: string) => {
    setSelectedMessageIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ── Share helpers ─────────────────────────────────────────────────────
  const showShareToast = useCallback((msg: string) => {
    coachShow({ id: 'share', kind: 'toast', body: msg, ttlMs: 2200 });
  }, []);

  const formatQaAsMarkdown = useCallback((question: { text: string }, answers: { role: string; text: string }[], title?: string) => {
    const lines: string[] = [];
    if (title) lines.push(`# ${title}\n`);
    lines.push(`## Question\n\n${question.text}\n`);
    lines.push(`## Answer\n`);
    for (const a of answers) {
      if (a.role === 'system-action') lines.push(`\n> *${a.text}*\n`);
      else lines.push(`\n${a.text}\n`);
    }
    lines.push(`\n---\n*Shared from ${getBrand().name} — ${new Date().toLocaleDateString()}*`);
    return lines.join('\n');
  }, []);

  /** Open the unified share modal with a typed payload */
  const doSharePayload = useCallback((payload: SharePayload) => {
    pendingSharePayloadRef.current = payload;
    pendingShareTextRef.current = payload.text ?? null;
    pendingShareTargetRef.current = null;
    pendingAutoSubmitRef.current = /^\s*\/plan execute\b/.test(payload.text ?? '');
    setShareTargetOpen(true);
  }, []);

  /** Open the share target picker with the formatted text ready to dispatch (legacy convenience) */
  const doShare = useCallback((text: string) => {
    doSharePayload({ type: 'text', text });
  }, [doSharePayload]);

  /** Prefill the input with a plan execute command (from PlanChip "Run here") */
  const handleRunPlanHere = useCallback((command: string) => {
    if (currentSessionIdRef.current) {
      useComposerStore.getState().appendOrReplaceDraft(currentSessionIdRef.current, command, 'replace');
    }
    document.dispatchEvent(new CustomEvent('focus-composer'));
  }, []);

  /** Navigate to chat and prefill input with work item context (from WorkItemCard "Work on this") */
  const handleWorkOnItem = useCallback((wi: { kind: string; number: number; title: string; repo: string; url: string }) => {
    goToChat();
    const verb = wi.kind === 'pr' ? 'Review and work on PR' : 'Work on issue';
    const prompt = `${verb} #${wi.number} in ${wi.repo}: ${wi.title}\n${wi.url}`;
    setTimeout(() => {
      if (currentSessionIdRef.current) {
        useComposerStore.getState().appendOrReplaceDraft(currentSessionIdRef.current, prompt, 'replace');
      }
      document.dispatchEvent(new CustomEvent('focus-composer'));
    }, 150);
  }, [goToChat]);

  /** Native phone share / clipboard fallback — called from ShareTargetModal "Share externally" */
  const doShareExternal = useCallback((text: string) => {
    if (navigator.share) {
      navigator.share({ text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text).then(() => showShareToast('Copied to clipboard')).catch(() => showShareToast('Copy failed'));
    }
  }, [showShareToast]);

  const handleShareMedia = useCallback(async (src: string) => {
    const rasterizeSvg = async (svgBlob: Blob): Promise<{ blob: Blob; ext: string }> => {
      const svgUrl = URL.createObjectURL(svgBlob);
      try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const el = new Image();
          el.onload = () => resolve(el);
          el.onerror = reject;
          el.src = svgUrl;
        });
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 1200;
        canvas.height = img.naturalHeight || 800;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const pngBlob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(b => b ? resolve(b) : reject(new Error('canvas toBlob failed')), 'image/png');
        });
        return { blob: pngBlob, ext: 'png' };
      } finally {
        URL.revokeObjectURL(svgUrl);
      }
    };

    const getShareable = async (rawBlob: Blob): Promise<{ blob: Blob; ext: string }> => {
      if (rawBlob.type.includes('svg')) return rasterizeSvg(rawBlob);
      const ext = rawBlob.type.includes('png') ? 'png' : rawBlob.type.includes('gif') ? 'gif' : 'jpg';
      return { blob: rawBlob, ext };
    };

    if (navigator.share && (navigator as any).canShare) {
      try {
        const res = await fetch(src);
        const rawBlob = await res.blob();
        const { blob, ext } = await getShareable(rawBlob);
        const file = new File([blob], `valko-image.${ext}`, { type: blob.type });
        if ((navigator as any).canShare({ files: [file] })) {
          await navigator.share({ files: [file] });
          return;
        }
      } catch {}
    }
    // Fallback: download — derive extension from blob type
    try {
      const res = await fetch(src);
      const rawBlob = await res.blob();
      const { blob, ext } = await getShareable(rawBlob);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `image-${Date.now()}.${ext}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch {
      const a = document.createElement('a');
      a.href = src;
      a.download = `image-${Date.now()}.png`;
      a.click();
    }
  }, []);

  // Listen for share-media events from global ImageLightbox
  useEffect(() => {
    const handler = (e: Event) => {
      const src = (e as CustomEvent<string>).detail;
      if (src) handleShareMedia(src);
    };
    window.addEventListener('share-media', handler);
    return () => window.removeEventListener('share-media', handler);
  }, [handleShareMedia]);

  // Listen for share-text-snippet events from QuotePill Share action
  useEffect(() => {
    const handler = (e: Event) => {
      const snippet = (e as CustomEvent<string>).detail;
      if (snippet) doShare(snippet);
    };
    document.addEventListener('share-text-snippet', handler);
    return () => document.removeEventListener('share-text-snippet', handler);
  }, [doShare]);

  const handleContextLoaded = useCallback((projects: string[]) => {
    for (const key of projects) checkProject(key);
  }, [checkProject]);

  const [sshPickerOpen, setSshPickerOpen] = useState(false);
  const sshTermRef = useRef<SshTerminalHandle | null>(null);
  const openLogbook = useCallback(() => {
    window.history.pushState({ vaView: activeViewRef.current, modal: 'logbook' }, '', '#logbook');
    setLogbookOpen(true);
  }, []);
  const closeLogbook = useCallback(() => {
    setLogbookOpen(false);
    if (window.history.state?.modal === 'logbook') {
      window.history.back();
    }
  }, []);
  // settings-zustand-migration Phase 2: beetleVisible sourced from the store
  // (migrated from legacy 'logbook-beetle-visible' on first boot).
  const beetleVisible = useSettingsStore((s) => s.beetleVisible);

  /** Latest TTS gates for socket callbacks (avoid stale closure; currentSessionId comes from useSocket). */
  const ttsGateRef = useRef({
    autoSpeak: false,
    currentSessionId: null as string | null,
    isSessionSoundOn: (_id: string): boolean => true,
    sshConnected: false,
  });

  const handleChunk = useCallback((chunk: string) => {
    const g = ttsGateRef.current;
    if (!g.autoSpeak) return;
    if (isMicRecording(useComposerStore.getState().globalRecording.sessionId)) return; // self-echo guard
    if (!g.currentSessionId || !g.isSessionSoundOn(g.currentSessionId)) return;
    feedChunk(chunk);
  }, [feedChunk]);

  const handleDone = useCallback(() => {
    const g = ttsGateRef.current;
    if (!g.autoSpeak) return;
    if (isMicRecording(useComposerStore.getState().globalRecording.sessionId)) return; // self-echo guard
    if (!g.currentSessionId || !g.isSessionSoundOn(g.currentSessionId)) return;
    flush();
  }, [flush]);

  const handleSummaryReady = useCallback(
    (summary: string) => {
      const g = ttsGateRef.current;
      if (!g.autoSpeak) return;
      if (isMicRecording(useComposerStore.getState().globalRecording.sessionId)) return; // self-echo guard
      if (g.sshConnected) {
        speak(summary);
        return;
      }
      if (g.currentSessionId && !g.isSessionSoundOn(g.currentSessionId)) return;
      speak(summary);
    },
    [speak],
  );

  const {
    connected, deployStatus, deployPending, lastDeploy, messages, summarizing, sessionsSynced,
    sessions, currentSessionId, toolActivities, inflightToolRowId, sessionMeta, usage,
    sessionHealth, fetchSessionHealth,
    sendWithAttachments, newSession, replayPersonaGreeting,
    sendText, submitAgentAnswer, listSessions, switchSession, renameSession, deleteSession,
    updateSessionTuning,
    cancelPrompt, transcribeAudio, sendAudioChunk,
    openTabs, switchTab, openInTab, closeTab,
    hasMore, loadOlderMessages, clearChat,
    sshConnected, sshTarget, sshOutput, sshOutputEpoch, sshTargets, sshVoiceHistory,
    fetchSshTargets, sshConnect, sshDisconnect, sshExitTerminal, sshSendInput, sshResize,
    requestSshSummary,
    providerInfo,
    providerErrors,
    sendTextWithOverride,
    clearProviderError,
    emitOpusConsult, emitOpusDismissed,
    activeConsultation,
    toggleAutoDocsPaused,
    updateAutoDocsThreshold,
    userAutoDocsThreshold,
    pinProject, unpinProject,
    pinSessionToList, unpinSessionFromList,
    resetContext,
    setPendingPrompt,
    socketRef,
  } = useSocket({
    enabled: authenticated,
    onSummaryReady: handleSummaryReady,
    onChunk: handleChunk,
    onDone: handleDone,
    onSessionResumeFailed: handleResumeFailed,
    onContextLoaded: handleContextLoaded,
    onAutoSwitch: () => goToChat(),
    onOpusSuggested: useCallback((payload: { sessionId: string; prompt: string; reason: string }) => {
      goToChat();
      setPendingOpusSuggestion({ prompt: payload.prompt, source: 'classifier' });
    }, [goToChat]),
    maxTabs: maxOpenTabs,
    onTabLimitReached: useCallback((max: number) => {
      coachShow({ id: 'session-limit', kind: 'toast', body: `Session limit reached (${max} open). Close one to start another.`, ttlMs: 4000 });
    }, []),
    onSshDisconnected: useCallback(() => goToChat(), [goToChat]),
    onBrowserConsentRequired: useCallback((payload: { sessionId: string; version: string }) => {
      setBrowserConsentPrompt(payload);
    }, []),
    onBrowserTierGateRequired: useCallback((payload: { sessionId: string; reason: string }) => {
      setBrowserTierGatePrompt(payload);
    }, []),
    // Phase 9 cascade tier-gate: the backend denied a cascade session, so open (or swap to)
    // the Agents/sessions drawer — SessionListView's tier-denial picker + hint mount only
    // when the 'sessions' panel is open. Issue #1203.
    //
    // Routes through the named `openForCascadeGate()` store action (R-A2), which sets a
    // transient `cascadeGatePending` cue that SessionListView reads REACTIVELY — so the hint
    // survives a fresh mount even when the drawer was closed / on another panel (the
    // fix/cascade-gated-drawer-panel-swap reliability fix; a synchronous window event was
    // missed by the not-yet-mounted listener). We still push the same history entry
    // openDrawerH('sessions') would (so Back closes the drawer consistently), then call the
    // named action.
    //
    // platform-isolation #1657 Phase 2 (Slice A): useSocket calls this handler DIRECTLY
    // (the `onCascadeGated` option) instead of dispatching a global `cascade-gated`
    // CustomEvent — the producer→consumer hop is now a typed callback, not the event bus.
    onCascadeGated: useCallback(() => {
      window.history.pushState(
        { vaView: activeViewRef.current, modal: 'sessions' },
        '',
        '#sessions',
      );
      openForCascadeGate();
    }, []),
  });

  // ── Post-useSocket: sync placeholder refs + URL helpers ──

  // Busy state is derived from composerStore.outbound[].mode for the active
  // session (busy-to-outbound-migration Phase 1). Replaces the legacy
  // tabCache.busy mirror — components downstream are unchanged because the
  // variable name + semantics are preserved.
  const busy = useIsBusy(currentSessionId);

  // Stuck-session safety guard (busy-to-outbound-migration Phase 4).
  // Replaces the per-tab setTimeout that lived inside setTabBusy. Polls every
  // 60s and force-fails any outbound entry stuck in `submitting` for >5 min.
  // Reason 'no_ack' preserved — OutboundState union is unchanged.
  useEffect(() => {
    const id = setInterval(() => {
      const state = useComposerStore.getState();
      const now = Date.now();
      state.sessions.forEach((region, sid) => {
        region.outbound.forEach((entry) => {
          if (entry.mode === 'submitting' && entry.submittedAt && now - entry.submittedAt > 300_000) {
            state.markSendFailed(sid, entry.clientMsgId, 'no_ack');
          }
        });
      });
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  // Sync the refs used by the early popstate handler (declared before useSocket to avoid TDZ).
  useEffect(() => { openTabsRef.current = openTabs; }, [openTabs]);
  useEffect(() => { switchTabRef.current = switchTab; }, [switchTab]);
  // The "home" session for back-stack re-anchoring (standalone exit-guard): the
  // last-active session, falling back to the first open tab. null → zero-session
  // empty-state (the #chat placeholder anchors there).
  useEffect(() => {
    currentSessionIdHomeRef.current = currentSessionId ?? openTabs[0] ?? null;
    currentSessionIdRef.current = currentSessionId ?? null;
  }, [currentSessionId, openTabs]);

  // When in chat and URL is still the generic '#chat' placeholder (e.g. during new-session
  // creation), replace it with the proper per-session URL once currentSessionId resolves.
  useEffect(() => {
    if (activeView !== 'chat' || !currentSessionId) return;
    if (window.location.hash === '#chat') {
      window.history.replaceState(
        { vaView: 'chat', sessionId: currentSessionId },
        '',
        buildSessionHash(currentSessionId),
      );
    }
  }, [activeView, currentSessionId]);

  // Cold-load stale-session drift guard (regression-followups-2026-06-10 item E):
  // mirrors the popstate drift guard but for the initial deep-link / persisted-session
  // restore where no popstate fires. Once the authoritative server session list has
  // loaded (sessions non-empty), if the chat's currentSessionId is one the server
  // doesn't know about, switch to a surviving session (Phase 2: chat is home, there is
  // NO list view to fall back to). If no session resolves, the zero-session empty-state
  // renders. Gated to fire once and only after the server snapshot arrives, so a valid
  // deep link is never bounced during the pre-SESSIONS_LIST loading window.
  const coldLoadDriftCheckedRef = useRef(false);
  useEffect(() => {
    if (coldLoadDriftCheckedRef.current) return;
    if (sessions.length === 0) return; // server snapshot not loaded yet
    coldLoadDriftCheckedRef.current = true;
    if (activeViewRef.current !== 'chat') return;
    if (!currentSessionId || !sessions.some((s) => s.id === currentSessionId)) {
      // Drifted: pick the first surviving open tab, else the first server session, and
      // open it in chat (inlined openInTab+goToChat — handleSelectSession is declared
      // later in this component, so referencing it here would TDZ).
      const survivor = openTabs.find((id) => sessions.some((s) => s.id === id)) ?? sessions[0]?.id ?? null;
      if (survivor) { openInTab(survivor); goToChat(survivor); }
    }
  }, [sessions, currentSessionId, openTabs, openInTab, goToChat]);

  // inputbar-text-shadow-migration Phase 1+2: draft prop drilling fully
  // removed. InputBar subscribes to composerStore directly via currentSessionId
  // and writes via composerStore.setDraft. App.tsx no longer reads or writes
  // the per-session draft.
  // Expanded compose state for the active session — restored when switching back to a session.
  const activeComposeExpanded = useComposerStore((s) => getSessionRegion(s, currentSessionId ?? '').input.composeExpanded);
  // All session regions + global recording — used to derive per-tab pending indicators.
  const composerSessions = useComposerStore((s) => s.sessions);
  const globalRecordingSessionId = useComposerStore((s) => s.globalRecording.sessionId);
  const orchestrationGated = useComposerStore((s) => s.orchestrationGated);
  const idleSet = useAgentListStore((s) => s.idleSet);

  // Phase 3 (agents-drawer-carousel-reorg.md): the single source of truth for the
  // VISIBLE (non-slept) dot set. The dots strip renders `visibleDots`, and the swipe
  // carousel + prev/next slivers cycle through `visibleDotTabs` — the SAME set, so a
  // swipe can never land on a slept agent that has no dot. Memoised on every input that
  // can change visibility (incl. idleSet + composer state): sleeping a *background*
  // agent mutates idleSet without touching openTabs/currentSessionId, so keying the
  // sliver effect on a stale [openTabs, currentSessionId] dep would leave the sliver
  // previewing a now-slept neighbour while the swipe lands on the next visible one.
  const visibleDots = useMemo(
    () =>
      getVisibleDots(
        openTabs,
        currentSessionId ?? '',
        {
          sessions: composerSessions,
          globalRecording: { sessionId: globalRecordingSessionId, startedAt: null },
          orchestrationGated,
        },
        idleSet,
      ),
    [openTabs, currentSessionId, composerSessions, globalRecordingSessionId, orchestrationGated, idleSet],
  );
  const visibleDotTabs = useMemo(() => visibleDots.map((d) => d.tabId), [visibleDots]);

  // Long-press a background dot → confirm dialog → sleep. Holds the pending target.
  const [sleepConfirm, setSleepConfirm] = useState<{ sessionId: string } | null>(null);

  // Invariant: the session you're viewing can't also be "asleep". Auto-wake on
  // focus so opening a slept session from the recent list (or swiping to it)
  // returns it to the dot strip — this is the wake half of the sleep gesture.
  useEffect(() => {
    if (currentSessionId && useAgentListStore.getState().idleSet.has(currentSessionId)) {
      useAgentListStore.getState().wake(currentSessionId);
    }
  }, [currentSessionId]);

  // inputbar-text-shadow-migration Phase 2: `handleDraftChange` removed.
  // InputBar now writes draft directly to composerStore.setDraft via its
  // local setText closure (Phase 1). The session-scoped capture that lived
  // here is now baked into setText via the `currentSessionId` dep.

  const handleExpandedChange = useCallback((expanded: boolean) => {
    if (currentSessionId) {
      useComposerStore.getState().setComposeExpanded(currentSessionId, expanded);
    }
  }, [currentSessionId]);

  // Debounced pushState for swipe-triggered session switches.
  // Rapid swipes (e.g. flick through 5 sessions) only create ONE history entry
  // for the session where the user settles. The 300 ms quiet period matches
  // what feels natural on mobile.
  const switchTabUrlTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const osBackToSessionList = useSettingsStore((s) => s.osBackToSessionList ?? true);
  const osBackToSessionListRef = useRef(osBackToSessionList);
  useEffect(() => { osBackToSessionListRef.current = osBackToSessionList; }, [osBackToSessionList]);
  const switchTabWithHistory = useCallback((sessionId: string) => {
    useComposerStore.getState().updateLastSeenAt(sessionId, Date.now());
    switchTab(sessionId);
    if (switchTabUrlTimerRef.current) clearTimeout(switchTabUrlTimerRef.current);
    switchTabUrlTimerRef.current = setTimeout(() => {
      const alreadyInChatEntry = window.history.state?.vaView === 'chat';
      const stateObj = { vaView: 'chat', sessionId };
      const url = buildSessionHash(sessionId);
      if (osBackToSessionListRef.current && alreadyInChatEntry) {
        window.history.replaceState(stateObj, '', url);
      } else {
        window.history.pushState(stateObj, '', url);
      }
    }, 300);
  }, [switchTab]);

  // Keep prevNextBadgeRef up-to-date for the swipe sliver (read during touchmove, no re-render).
  useEffect(() => {
    // Phase 3: compute over the VISIBLE (non-slept) set so the sliver previews the same
    // neighbour the swipe will land on. visibleDotTabs is the dep (not openTabs): it
    // changes identity when idleSet/composer state changes, so sleeping a *background*
    // agent re-fires this effect and the sliver stops painting the now-slept neighbour.
    const idx = visibleDotTabs.indexOf(currentSessionId ?? '');
    const compute = (id: string | undefined) => {
      if (!id) return null;
      const s = sessions.find((s) => s.id === id);
      const m = s?.model ?? null;
      const letter = m?.includes('opus') ? 'O' : m?.includes('sonnet') ? 'S' : m?.includes('haiku') ? 'H' : m?.includes('gemini') ? 'G' : 'S';
      const color  = m?.includes('opus') ? '#bc8cff' : m?.includes('sonnet') ? '#56a0d4' : m?.includes('haiku') ? '#8888aa' : m?.includes('gemini') ? '#34a853' : '#4ec9b0';
      return { letter, color };
    };
    // Phase 2 wraparound: the prev/next slivers show the CYCLIC neighbour (the same
    // target the swipe will land on), so at idx 0 the right-swipe sliver shows
    // visibleDotTabs[n-1] and at idx n-1 the left-swipe sliver shows visibleDotTabs[0] —
    // no dead boundary. A single tab (n<=1) has no neighbour → both null.
    const n = visibleDotTabs.length;
    const hasNeighbour = idx !== -1 && n > 1;
    prevNextBadgeRef.current = {
      prev: hasNeighbour ? compute(visibleDotTabs[nextSessionIndex(idx, n, 'prev')]) : null,
      next: hasNeighbour ? compute(visibleDotTabs[nextSessionIndex(idx, n, 'next')]) : null,
    };
  }, [visibleDotTabs, currentSessionId, sessions]);

  // ── First-time swipe hint → unified CoachStore (coach-tooltip-system.md Phase 1) ──
  // The hint is now a `coach.offer('swipe')` row rendered by <CoachLayer/>. The old
  // `.swipe-hint-toast` div + its dedicated localStorage gate are gone; the store's
  // `seen` map (seeded on rehydrate from the legacy `voice-assistant-swipe-hinted` key)
  // is the SINGLE source of "already seen". We keep only the per-user chat-view-entry
  // counter that feeds snapshot.chatViewEntries — the store's `swipe` reachableWhen
  // (chatViewEntries >= 3 && swipeableAgents >= 2) does the actual gating.
  const SWIPE_COUNT_KEY = 'voice-assistant-swipe-hint-count';
  // The swipe-to-switch gesture is mobile-only — <CoachLayer/> mounts in BOTH shells
  // (the substrate is shared), but the swipe HINT must not surface on desktop where the
  // gesture doesn't apply. Gate the swipe offer on the canonical shell selector.
  const isDesktop = useIsDesktop();

  // Arm coaching after onboarding settles (armed is runtime-only → re-armed each launch).
  // evaluate() no-ops until then, so no coach can stack on a first-run modal wall.
  useEffect(() => {
    const t = setTimeout(() => coachArm(), 4000);
    return () => clearTimeout(t);
  }, []);

  // Each chat-view entry with ≥2 SWIPEABLE agents (mobile only): bump the counter, offer
  // the swipe coach, and let the store decide (armed + reachableWhen + not-seen). Gate on
  // visibleDotTabs (the non-slept set the swipe actually cycles), not raw openTabs — with
  // e.g. [active, slept] the swipe is a no-op, so don't promise "swipe to switch agents".
  // Calling offer+evaluate every entry is safe: offer dedupes by id, evaluate is a pure scan.
  useEffect(() => {
    if (isDesktop || activeView !== 'chat' || visibleDotTabs.length < 2) return;
    let count = 3;
    try {
      count = parseInt(localStorage.getItem(SWIPE_COUNT_KEY) ?? '0', 10) + 1;
      localStorage.setItem(SWIPE_COUNT_KEY, String(count));
    } catch { /* storage blocked → treat as reachable */ }
    coachOffer('swipe');
    const snapshot: CoachSnapshot = {
      chatViewEntries: count,
      swipeableAgents: visibleDotTabs.length,
      fabVisible: false,
      newSessionsCreated: 0,
      assistantMsgCount: 0,
      composerFocusedEmptyMs: 0,
      drawerOpenedCount: 0,
      sessionCount: visibleDotTabs.length,
      mountedAnchors: deriveMountedAnchors(),
    };
    coachEvaluate(snapshot);
  }, [activeView, visibleDotTabs.length, isDesktop]);

  // ── Newcomer-journey coachWatcher (coach-tooltip-system.md Phase 4, §5.2) ──
  // The general tick for the non-swipe, non-ptt catalog rows (model-picker, double-
  // tap-quote, reader-mode, agents-nav). ptt is handled entirely inside InputBar.tsx
  // (the only row needing elapsed-time tracking); swipe keeps its own effect above
  // (chat-view-only, mobile-only). newSessionsCreatedRef/drawerOpenedCountRef are
  // runtime counters — no persistence needed, they only feed reachableWhen predicates
  // for the life of this launch.
  const newSessionsCreatedRef = useRef(0);
  const drawerOpenedCountRef = useRef(0);
  const assistantMsgCount = useMemo(
    () => messages.filter((m) => m.role === 'assistant').length,
    [messages],
  );
  // Reuses the `drawerPanel` selector already declared above (useDrawerPanel()) —
  // the Agents drawer opening (panel==='sessions') is what makes the FAB visible.

  const runCoachWatcherTick = useCallback((fabVisible: boolean) => {
    coachOffer('model-picker');
    coachOffer('double-tap-quote');
    coachOffer('reader-mode');
    coachOffer('agents-nav');
    const snapshot: CoachSnapshot = {
      chatViewEntries: 0,
      swipeableAgents: 0,
      fabVisible,
      newSessionsCreated: newSessionsCreatedRef.current,
      assistantMsgCount,
      composerFocusedEmptyMs: 0,
      drawerOpenedCount: drawerOpenedCountRef.current,
      sessionCount: sessions.length,
      mountedAnchors: deriveMountedAnchors(),
    };
    coachEvaluate(snapshot);
  }, [assistantMsgCount, sessions.length]);

  // Ticks on: assistant message arriving (reader-mode/double-tap-quote), session
  // count crossing a threshold (agents-nav), or a new session being created
  // (model-picker — though fabVisible is false here; the drawer-open effect below
  // gives model-picker its real chance once the FAB is actually visible).
  useEffect(() => {
    runCoachWatcherTick(false);
  }, [assistantMsgCount, sessions.length, runCoachWatcherTick]);

  // The FAB (and thus model-picker's anchor) only exists inside the Agents drawer's
  // sessions panel — NOT a routable 'list' view anymore (agents-drawer-carousel-reorg
  // Phase 1). Declared BEFORE the tick effect above runs its snapshot read in the same
  // commit is not required here (separate effects, separate deps) — the increment only
  // needs to land before the NEXT tick, which this effect's own tick provides.
  useEffect(() => {
    if (drawerPanel !== 'sessions') return;
    drawerOpenedCountRef.current += 1;
    // Real usage of Agents nav (opening the drawer) — cancel the "your chats live
    // under Agents" tip mid-display + mark seen (usage-implies-discovery, §5.3).
    coachMarkDiscovered('agents-nav');
    runCoachWatcherTick(true);
  }, [drawerPanel, runCoachWatcherTick]);

  // ── PWA install prompt (Chrome/Android; iOS doesn't fire beforeinstallprompt) ──
  useEffect(() => {
    if (isStandaloneMode) return;
    const handler = (e: Event) => {
      e.preventDefault();
      installPromptRef.current = e as unknown as { prompt: () => void; userChoice: Promise<{ outcome: string }> };
      setInstallPromptAvailable(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallPrompt = useCallback(async () => {
    if (!installPromptRef.current) return;
    installPromptRef.current.prompt();
    const result = await installPromptRef.current.userChoice;
    if (result.outcome === 'accepted') {
      setInstallPromptAvailable(false);
      installPromptRef.current = null;
    } else {
      // User dismissed the browser prompt — don't pester them again for 30 days.
      dismissInstallPill();
    }
  }, [dismissInstallPill]);

  // Mirror pill eligibility into the coach store (Phase 3b) — the "when to offer"
  // logic (available + not standalone + not 30-day-dismissed) stays here as the
  // single source of truth; CoachLayer renders it generically via action/onDismiss.
  // kind:'pill' → sticky (no ttl); dismissInstallPill both writes the 30-day
  // cooldown AND (via onDismiss) clears the coach slot.
  useEffect(() => {
    if (installPromptAvailable && !isStandaloneMode && !installDismissed) {
      coachShow({
        id: 'pwa-install',
        kind: 'pill',
        body: `Install ${getBrand().name} for quick access`,
        action: { label: `⊕ Install ${getBrand().name}`, onClick: handleInstallPrompt },
        onDismiss: dismissInstallPill,
      });
    } else {
      coachDismiss('pwa-install');
    }
  }, [installPromptAvailable, installDismissed, handleInstallPrompt, dismissInstallPill]);

  // ── Share helpers (need messages/sessions/currentSessionId from useSocket above) ──
  const getQaPair = useCallback((msgIdx: number) => {
    const question = messages[msgIdx];
    const answers = [];
    for (let j = msgIdx + 1; j < messages.length; j++) {
      if (messages[j].role === 'user') break;
      if (messages[j].role === 'assistant' || messages[j].role === 'system-action') answers.push(messages[j]);
    }
    return { question, answers };
  }, [messages]);

  const formatMultiMessageMarkdown = useCallback((ids: Set<string>, title?: string) => {
    const selected = messages.filter(m => m.id && ids.has(m.id));
    const lines: string[] = [];
    if (title) lines.push(`# ${title}\n`);
    for (const msg of selected) {
      if (msg.role === 'user') lines.push(`\n**You:** ${msg.text}\n`);
      else if (msg.role === 'assistant') lines.push(`\n**Assistant:**\n${msg.text}\n`);
      else if (msg.role === 'system-action') lines.push(`\n> *${msg.text}*\n`);
    }
    lines.push(`\n---\n*Shared from ${getBrand().name} — ${new Date().toLocaleDateString()}*`);
    return lines.join('\n');
  }, [messages]);

  const handleShareSingle = useCallback(async (msgIdx: number) => {
    // Quota gate: check max_shares before proceeding
    const quota = await checkQuota('max_shares');
    if (!quota.allowed) {
      const msg = quota.limit !== null
        ? `You've used all ${quota.limit} shares today. Upgrade to Pro for unlimited.`
        : 'Sharing is currently disabled.';
      showShareToast(msg);
      return;
    }
    const { question, answers } = getQaPair(msgIdx);
    const session = sessions.find(s => s.id === currentSessionId);
    const title = session?.name || session?.previewText || undefined;
    doShare(formatQaAsMarkdown(question, answers, title));
    // Increment usage counter after successful share trigger
    incrementQuota('max_shares');
  }, [getQaPair, formatQaAsMarkdown, doShare, sessions, currentSessionId, showShareToast]);

  const enterShareSelection = useCallback((msgIdx: number) => {
    setSelectionMode(true);
    setSelectionPurpose('share');
    setOpusPrompt('');
    setOpusSuggestionSource(null);
    const { question, answers } = getQaPair(msgIdx);
    const ids = new Set<string>([question, ...answers].map(m => m.id).filter(Boolean) as string[]);
    setSelectedMessageIds(ids);
  }, [getQaPair]);

  ttsGateRef.current = {
    autoSpeak,
    currentSessionId,
    isSessionSoundOn,
    sshConnected,
  };

  const prevSessionForTtsRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevSessionForTtsRef.current;
    if (prev !== null && prev !== currentSessionId) {
      stop();
      // Exit selection mode when switching sessions — prevents the overlay from
      // orphaning to the wrong session after a tab switch.
      if (selectionMode) exitSelectionMode();
    }
    prevSessionForTtsRef.current = currentSessionId;
  }, [currentSessionId, stop, selectionMode, exitSelectionMode]);

  // Apply buffered Opus suggestion once messages are available
  useEffect(() => {
    if (!pendingOpusSuggestion) return;
    enterSelectionMode(pendingOpusSuggestion.prompt, pendingOpusSuggestion.source, messages);
    setPendingOpusSuggestion(null);
  }, [pendingOpusSuggestion, messages, enterSelectionMode]);

  // Inject pending share text into InputBar after session switch settles
  useEffect(() => {
    if (!currentSessionId || !pendingShareTextRef.current) return;
    const target = pendingShareTargetRef.current;
    if (target === 'new' || target === currentSessionId) {
      const text = pendingShareTextRef.current;
      const autoSubmit = pendingAutoSubmitRef.current;
      pendingShareTextRef.current = null;
      pendingShareTargetRef.current = null;
      pendingAutoSubmitRef.current = false;
      // Small delay to let the InputBar mount/reset before prefilling
      setTimeout(() => {
        if (currentSessionIdRef.current) {
          useComposerStore.getState().appendOrReplaceDraft(currentSessionIdRef.current, text, 'replace');
        }
        document.dispatchEvent(new CustomEvent('focus-composer'));
        if (autoSubmit) {
          // Additional delay to let the textarea value settle before submitting
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent('auto-submit'));
          }, 150);
        }
      }, 350);
    }
  }, [currentSessionId]);

  // Fallback health fetch: if health is still null 1s after a session switch
  // (e.g. cached-tab switch that skips SWITCH_SESSION), request it explicitly.
  useEffect(() => {
    if (!currentSessionId || sessionHealth !== null) return;
    const t = setTimeout(() => {
      if (sessionHealth === null) fetchSessionHealth();
    }, 1000);
    return () => clearTimeout(t);
  }, [currentSessionId, sessionHealth, fetchSessionHealth]);

  const handleSpeakText = useCallback(
    (text: string, key: string) => {
      if (!autoSpeak) return;
      if (isMicRecording(globalRecordingSessionId)) return; // self-echo guard
      if (!currentSessionId || !isSessionSoundOn(currentSessionId)) return;
      speak(text, key);
    },
    [autoSpeak, globalRecordingSessionId, currentSessionId, isSessionSoundOn, speak],
  );

  const chatTtsAllowed =
    autoSpeak && !!currentSessionId && isSessionSoundOn(currentSessionId) &&
    !isMicRecording(globalRecordingSessionId);

  // Self-echo prevention (a): when the mic goes hot (any session — Whisper or native
  // Web Speech), stop audio already playing so the live mic doesn't capture the app's
  // own output. Reader Mode stops itself (it owns a separate TTS instance) via its own
  // effect in ReaderOverlay. Half (b) — not STARTING new auto-read — is the gates above.
  useEffect(() => {
    if (!isMicRecording(globalRecordingSessionId)) return;
    stop();                            // chat TTS (shared instance)
    useDocTtsStore.getState().stop();  // document TTS
  }, [globalRecordingSessionId, stop]);

  // Safety: globalRecording is persisted (composerStore). A crash/reload could leave
  // sessionId set, which — with the suppression above — would permanently silence ALL
  // narration. No mic survives a page load, so clear the flag once on mount.
  useEffect(() => {
    useComposerStore.getState().setGlobalRecording(null, null);
  }, []);

  // P4 (#1652): the "play latest" pill is retired — per-message 🔊 (docTtsStore) is
  // the sole manual read-aloud surface. `onPlayLast` / `hasLastAssistant` /
  // `docReadActive` (the pill's play-latest + priority-disable wiring) are gone with it.
  // Read-aloud priority over streaming auto-read is still enforced at the source by
  // useTTS.speak()/feedChunk()'s isDocTtsReadActive() guard (see SE-13).

  // Double-tap on chat whitespace (not on a message bubble) → focus BottomComposer
  const onChatAreaDoubleTap = useDoubleTap(useCallback((e: PointerEvent) => {
    if (selectionMode) return;
    const target = e.target as Element;
    if (target.closest('.msg, .chat-fab-row, .load-older-btn, .turn-nav-cluster, .scroll-to-bottom-fab, button')) return;
    const ta = document.querySelector('.bottom-composer textarea, .bottom-composer input[type="text"]') as HTMLElement | null;
    ta?.focus();
  }, [selectionMode]));

  useEffect(() => {
    const chatArea = document.querySelector('.chat-area');
    if (!chatArea) return;
    chatArea.addEventListener('pointerdown', onChatAreaDoubleTap as EventListener, { passive: true });
    return () => chatArea.removeEventListener('pointerdown', onChatAreaDoubleTap as EventListener);
  }, [onChatAreaDoubleTap]);

  // "Double-tap to compose" hint — shown once per session until the user has used it 5 times.
  // sessionStorage ensures it shows on each fresh page load; the counter lets it fade out naturally.
  useEffect(() => {
    if (!messages.length) return;
    if (sessionStorage.getItem('va-doubletap-hint-shown')) return;
    const timesShown = Number(localStorage.getItem('va-doubletap-hint-count') ?? '0');
    if (timesShown >= 5) return;
    sessionStorage.setItem('va-doubletap-hint-shown', '1');
    localStorage.setItem('va-doubletap-hint-count', String(timesShown + 1));
    coachShow({ id: 'compose-tip', kind: 'toast', body: 'Tip: double-tap chat or message to compose', ttlMs: 5000 });
  }, [!!messages.length]);

  const handleSaveChatTurnAsMemo = useCallback(async (userText: string, assistantText: string) => {
    setMemoSaving(true);
    try {
      const { authFetch } = await import('../api/authFetch');
      const res = await authFetch('/api/memos/from-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userText, assistantText }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err || res.statusText);
      }
      coachShow({ id: 'memo-saved', kind: 'toast', body: 'Saved exchange to Board memos', ttlMs: 4000 });
    } catch (e) {
      console.error('from-chat memo:', e);
      coachShow({ id: 'memo-save-failed', kind: 'toast', body: 'Could not save memo — try again', ttlMs: 4000, role: 'alert' });
    } finally {
      setMemoSaving(false);
    }
  }, []);

  const onViewportMemoSave = useCallback(() => {
    if (!viewportFocus?.memoPairUserText || !viewportFocus?.memoPairAssistantText) return;
    void handleSaveChatTurnAsMemo(
      viewportFocus.memoPairUserText,
      viewportFocus.memoPairAssistantText,
    );
  }, [viewportFocus, handleSaveChatTurnAsMemo]);

  useEffect(() => {
    setViewportFocus(null);
  }, [currentSessionId]);

  // Phase 2: the old `activeView === 'list'` listSessions trigger and the free-tier
  // "force out of list" effect are removed — there is no list VIEW anymore (chat is the
  // only view; the session list is the Agents drawer). The legacy session list still
  // populates via the on-connect LIST_SESSIONS emit in useSocket (so the carousel, dots
  // and top-bar name work without a list view), and the Agents drawer body fetches via
  // its own sessionListStore when opened.

  // Phase 2: swipe left/right is a TRUE WRAPAROUND CAROUSEL (no boundary, no "go to
  // list" — the list is the Agents drawer now). switchTabWithHistory is used instead of
  // switchTab so each settled swipe creates a history entry.
  // Phase 3: the carousel cycles `visibleDotTabs` (the non-slept set the dots show), NOT
  // raw openTabs — so a swipe traverses exactly the agents with a dot and never lands on
  // (and silently wakes) a slept one. The active session is always visible, so the hook's
  // `indexOf(current)` is never -1.
  useSwipeSession({
    openTabs: visibleDotTabs,
    currentSessionId,
    switchTab: switchTabWithHistory,
    enabled: activeView === 'chat',
    containerEl: chatPanelEl,
    onDrag: useCallback((offset: number) => applySlide(offset, false), [applySlide]),
    onDragEnd: useCallback((committed: boolean) => {
      applySlide(0, true); // snap back (animated) whether committed or bounce-back
      if (committed) {
        haptic(50);
        // A real swipe IS the discovery — seal the swipe coach (mark seen + cancel it
        // mid-display) so only the genuinely-stuck ever witness the hint. Guard so it's
        // not a setState on every swipe forever.
        if (!useCoachStore.getState().seen['swipe']) coachMarkDiscovered('swipe');
      }
    }, [applySlide]),
    onNearCommit: useCallback(() => haptic(20), []),
  });

  /** Select a session (from the Agents drawer) → open tab + route to its chat.
   *  goToChat replaceState-syncs the URL to #s/<id> even when already in chat, so the
   *  drawer-select path no longer leaves the dangling #sessions hash (Phase-2 stale-hash
   *  fix — reload then restores this chat, not the drawer). */
  const handleSelectSession = useCallback((sessionId: string) => {
    openInTab(sessionId);
    goToChat(sessionId);
  }, [openInTab, goToChat]);

  /** Continue button: open session + inject a "please continue" prompt */
  const handleContinueSession = useCallback((sessionId: string) => {
    openInTab(sessionId);
    goToChat(sessionId);
    setTimeout(() => sendText('Please continue where you left off.'), 150);
  }, [openInTab, goToChat, sendText]);

  /** Wrap newSession to also clear project tags and navigate to chat */
  const handleNewSession = useCallback((model?: string) => {
    const m = typeof model === 'string' ? model : undefined;
    newSession(m); // backend seeds the unique cosmic session name (session-name.util.ts)
    // Feeds model-picker's reachableWhen (fabVisible && newSessionsCreated >= 1) —
    // coach-tooltip-system.md §5.2.
    newSessionsCreatedRef.current += 1;
    clearAllTags();
    goToChat();
  }, [newSession, clearAllTags, goToChat]);

  /** Replay "?" (GearMenu/More, §5.4): re-arms the first-run coach rows (clears their
   *  `seen` + the persona-greeting ledger entry) and re-posts the persona intro into
   *  the current session's thread. */
  const handleReplayCoach = useCallback(() => {
    resetFirstRunCoaching();
    replayPersonaGreeting();
  }, [replayPersonaGreeting]);

  /** Replace current session: delete it then open a fresh one in the same window */
  const handleReplaceSession = useCallback((model?: string, docsUpdate?: 'quick' | 'full') => {
    if (currentSessionId) deleteSession(currentSessionId, docsUpdate);
    newSession(model); // backend seeds the unique cosmic session name
    clearAllTags();
    goToChat();
  }, [currentSessionId, deleteSession, newSession, clearAllTags, goToChat]);

  /** Open the reset-session confirmation dialog (+ button flow) */
  const openResetDialog = useCallback((model?: string) => {
    setResetPendingModel(model);
    setResetSaveKnowledge(true);
    setResetDialogOpen(true);
  }, []);

  /** User confirmed reset: truncate AI context in-place (stay in same session, preserve history) */
  const confirmReset = useCallback(() => {
    if (!currentSessionId) return;
    // Pass saveKnowledge to backend — when true it runs /handoff (which wraps /docs-update quick) before the reset
    resetContext(currentSessionId, resetSaveKnowledge);
    setResetDialogOpen(false);
  }, [resetSaveKnowledge, currentSessionId, resetContext]);

  const handleActAs = useCallback(() => {
    if (!currentSessionId) return;
    const currentSession = sessions.find((s: any) => s.id === currentSessionId);
    const current = currentSession?.actAsPersona || '';
    const email = prompt(
      current ? `Currently acting as: ${current}\n\nEnter email to impersonate (empty to clear):` : 'Enter email to impersonate as (for AKB debug):',
      current,
    );
    if (email === null) return;
    setActAsPersona(currentSessionId, email.trim() || null)
      .then(() => listSessions())
      .catch((e: any) => alert(`Failed: ${e.message}`));
  }, [currentSessionId, sessions, listSessions]);

  /** Open the delete-session confirmation dialog (from in-chat GearMenu) */
  const openDeleteDialog = useCallback(() => {
    setDeleteTargetIds([]);
    setDeleteSaveKnowledge(true);
    setDeleteDialogOpen(true);
  }, []);

  /** Open delete dialog for specific session IDs (from session list swipe or bulk select) */
  const openListDeleteDialog = useCallback((ids: string[]) => {
    setDeleteTargetIds(ids);
    // Bulk deletes cannot save knowledge — too expensive, not meaningful across N sessions
    setDeleteSaveKnowledge(ids.length <= 1);
    setDeleteDialogOpen(true);
  }, []);

  /** User confirmed delete */
  const confirmDelete = useCallback(() => {
    const docsUpdate = deleteSaveKnowledge ? 'quick' : undefined;
    if (deleteTargetIds.length > 0) {
      deleteTargetIds.forEach((id) => deleteSession(id, docsUpdate));
    } else if (currentSessionId) {
      // Phase 2: after deleting the active session, advance to a surviving open tab
      // (chat is home; there is no list view). If none remain, the zero-session
      // empty-state renders. Navigate BEFORE deleting so the target is current.
      const survivors = openTabs.filter((id) => id !== currentSessionId);
      const neighbour = survivors[0] ?? null;
      if (neighbour) { openInTab(neighbour); goToChat(neighbour); }
      deleteSession(currentSessionId, docsUpdate);
    }
    setDeleteDialogOpen(false);
    setDeleteTargetIds([]);
  }, [currentSessionId, deleteSaveKnowledge, deleteSession, deleteTargetIds, openTabs, openInTab, goToChat]);

  /** Generate a PDF from the pending share text and download/share it via the native share sheet or a direct download. */
  const handleShareAsPdf = useCallback(async () => {
    const text = pendingShareTextRef.current ?? '';
    if (!text) return;
    const session = sessions.find((s) => s.id === currentSessionId);
    const title = session?.name || session?.contextTag || undefined;
    const { authFetch } = await import('../api/authFetch');
    const res = await authFetch('/api/files/pdf/text', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, title }),
    });
    if (!res.ok) throw new Error(`PDF generation failed: HTTP ${res.status}`);
    const blob = await res.blob();
    const safeName = (title ?? 'chat-export').replace(/[^a-z0-9_-]/gi, '-').slice(0, 60);
    const pdfName = `${safeName}.pdf`;
    const pdfFile = new File([blob], pdfName, { type: 'application/pdf' });
    if ((navigator as any).canShare?.({ files: [pdfFile] })) {
      await navigator.share({ files: [pdfFile], title: pdfName });
    } else if (navigator.share) {
      const url = URL.createObjectURL(blob);
      await navigator.share({ title: pdfName, url });
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } else {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    }
    pendingShareTextRef.current = null;
    pendingShareTargetRef.current = null;
    pendingSharePayloadRef.current = null;
    exitSelectionMode();
  }, [sessions, currentSessionId, exitSelectionMode]);

  /** Generate a PDF from a file path and share/download it */
  const handleShareFilePdf = useCallback(async (opts: { filePath: string; includeImages: boolean; attachVideos: boolean; videoPaths: string[] }) => {
    const { authFetch } = await import('../api/authFetch');
    const params = new URLSearchParams({ path: opts.filePath });
    if (!opts.includeImages) params.set('includeImages', 'false');

    const res = await authFetch(`/api/files/pdf?${params}`);
    if (!res.ok) throw new Error(`PDF generation failed: HTTP ${res.status}`);
    const blob = await res.blob();
    const fileName = opts.filePath.split('/').pop() || 'file';
    const pdfName = fileName.replace(/\.(md|html|htm)$/, '.pdf');
    const files: File[] = [new File([blob], pdfName, { type: 'application/pdf' })];

    if (opts.attachVideos && opts.videoPaths.length > 0) {
      for (const vpath of opts.videoPaths) {
        try {
          const vres = await authFetch(vpath);
          if (vres.ok) {
            const vblob = await vres.blob();
            const vname = vpath.split('/').pop()?.split('?')[0] || 'video.mp4';
            files.push(new File([vblob], vname, { type: vblob.type || 'video/mp4' }));
          }
        } catch { /* skip */ }
      }
    }

    if ((navigator as any).canShare?.({ files })) {
      await navigator.share({ files, title: pdfName });
    } else if (navigator.share) {
      const url = URL.createObjectURL(blob);
      await navigator.share({ title: pdfName, url });
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } else {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    }
    pendingSharePayloadRef.current = null;
    pendingShareTextRef.current = null;
    pendingShareTargetRef.current = null;
  }, []);

  /** Share a file externally as raw text via native share or clipboard */
  const handleShareFileExternal = useCallback(async (filePath: string, fileName: string) => {
    const { authFetch } = await import('../api/authFetch');
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const textExts = new Set(['md', 'txt', 'json', 'yaml', 'yml', 'toml', 'csv', 'log', 'sh', 'ts', 'tsx', 'js', 'jsx', 'py', 'rs', 'go', 'java', 'sql', 'css', 'html', 'htm', 'xml', 'svg']);

    if (textExts.has(ext)) {
      // Text files: read as text, share/copy
      const res = await authFetch(`/api/files/read?path=${encodeURIComponent(filePath)}&full=1`);
      if (!res.ok) throw new Error(`Read failed: HTTP ${res.status}`);
      const data = await res.json();
      const text = (data.lines as string[]).join('\n');
      const mdFile = new File([text], fileName, { type: 'text/markdown' });
      if ((navigator as any).canShare?.({ files: [mdFile] })) {
        await navigator.share({ files: [mdFile], title: fileName });
      } else if (navigator.share) {
        await navigator.share({ title: fileName, text });
      } else {
        await navigator.clipboard.writeText(text);
        showShareToast('Copied to clipboard');
      }
    } else {
      // Binary files (.mp4, .pptx, .png, etc.): download via media endpoint
      const res = await authFetch(`/api/files/media?path=${encodeURIComponent(filePath)}`);
      if (!res.ok) throw new Error(`Download failed: HTTP ${res.status}`);
      const blob = await res.blob();
      const file = new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
      if ((navigator as any).canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: fileName });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        showShareToast('Download started');
      }
    }
    pendingSharePayloadRef.current = null;
    pendingShareTextRef.current = null;
    pendingShareTargetRef.current = null;
  }, [showShareToast]);

  /** Called by ShareTargetModal when user picks an existing session */
  const handleSendToSession = useCallback((sessionId: string) => {
    const payload = pendingSharePayloadRef.current;
    // For file payloads: prefill with a formatted file reference
    if (payload?.type === 'file' && payload.filePath) {
      const name = payload.fileName || payload.filePath.split('/').pop() || 'file';
      pendingShareTextRef.current = `Please review this file: ${name}\n\nFile path: ${payload.filePath}`;
    }
    if (!pendingShareTextRef.current) return;
    pendingShareTargetRef.current = sessionId;
    openInTab(sessionId);
    goToChat(sessionId);
  }, [openInTab, goToChat]);

  /** Called by ShareTargetModal when user picks "New session" */
  const handleShareToNewSession = useCallback(() => {
    const payload = pendingSharePayloadRef.current;
    if (payload?.type === 'file' && payload.filePath) {
      const name = payload.fileName || payload.filePath.split('/').pop() || 'file';
      pendingShareTextRef.current = `Please review this file: ${name}\n\nFile path: ${payload.filePath}`;
    }
    if (!pendingShareTextRef.current) return;
    pendingShareTargetRef.current = 'new';
    handleNewSession(); // navigates to chat + creates session; the pending-share effect writes the draft (appendOrReplaceDraft) on the new currentSessionId
  }, [handleNewSession]);

  /** Explain routing — "here" path: prefill current session compose */
  const handleExplainHere = useCallback(() => {
    if (!explainPayload) return;
    const text = buildExplainText(explainPayload.text);
    document.dispatchEvent(new CustomEvent('quote-source', { detail: { sourceEl: explainPayload.sourceEl } }));
    if (currentSessionIdRef.current) {
      useComposerStore.getState().appendOrReplaceDraft(currentSessionIdRef.current, text, 'replace');
    }
    document.dispatchEvent(new CustomEvent('focus-composer'));
    setExplainPayload(null);
  }, [explainPayload]);

  /** Explain routing — "another session" path */
  const handleExplainToSession = useCallback((sessionId: string) => {
    if (!explainPayload) return;
    pendingShareTextRef.current = buildExplainText(explainPayload.text);
    pendingShareTargetRef.current = sessionId;
    setExplainPayload(null);
    openInTab(sessionId);
    goToChat(sessionId);
  }, [explainPayload, openInTab, goToChat]);

  /** Explain routing — "new session with model" path */
  const handleExplainToNewSession = useCallback((modelId: string) => {
    if (!explainPayload) return;
    pendingShareTextRef.current = buildExplainText(explainPayload.text);
    pendingShareTargetRef.current = 'new';
    setExplainPayload(null);
    handleNewSession(modelId);
  }, [explainPayload, handleNewSession]);

  // handleNewRelay removed (Phase 5): superseded by handleNewSession('agy') / handleNewSession('claude-cli').

  // Pick up discuss-context from MemosPage (discuss buttons → ephemeral session)
  useEffect(() => {
    if (!connected) return;
    const raw = sessionStorage.getItem('discuss-context');
    if (!raw) return;
    sessionStorage.removeItem('discuss-context');
    try {
      const { projectKey, context } = JSON.parse(raw);
      if (context) {
        const prompt = projectKey
          ? `[Discussing ${projectKey}]\n\n${context}`
          : context;
        sendText(prompt);
        goToChat();
      }
    } catch {}
  }, [connected, sendText, goToChat]);

  // Plan choice button tap → send selection immediately, no popover
  useEffect(() => {
    const onExecute = (e: Event) => {
      const { cmd } = (e as CustomEvent<{ cmd: string }>).detail;
      if (cmd) sendText(cmd);
    };
    window.addEventListener('execute-slash-cmd', onExecute);
    return () => window.removeEventListener('execute-slash-cmd', onExecute);
  }, [sendText]);

  // AgentQuestionCard chip tap → resume the SDK via a discrete answer request
  // (NOT a normal send — see useSocket.submitAgentAnswer). sessionId is pinned at
  // the card so the answer routes to the asking session even after a swipe.
  useEffect(() => {
    const onAqnAnswer = (e: Event) => {
      const { answer, sessionId } = (e as CustomEvent<{ answer: string; sessionId?: string }>).detail;
      if (answer) submitAgentAnswer(answer, sessionId);
    };
    window.addEventListener('agent-question-answer', onAqnAnswer);
    return () => window.removeEventListener('agent-question-answer', onAqnAnswer);
  }, [submitAgentAnswer]);

  // Voice bug: transcript arrives after Whisper completes — inject to session or show toast
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !connected) return;
    const handler = (data: { id: string; transcript: string; intent: string }) => {
      if (data.intent === 'fix') {
        sendText(`[Bug Report] ${data.transcript}`);
        goToChat();
      } else {
        coachShow({ id: 'bug-queued', kind: 'toast', body: 'Bug queued for planning.', ttlMs: 4000 });
      }
    };
    socket.on(WsEvent.BUG_TRANSCRIBED, handler);
    return () => { socket.off(WsEvent.BUG_TRANSCRIBED, handler); };
  }, [connected, socketRef, sendText, goToChat]);

  // Slash command tap → show "Here / Elsewhere" popover bubble
  useEffect(() => {
    const onShow = (e: Event) => {
      const { cmd, x, y } = (e as CustomEvent<{ cmd: string; x: number; y: number }>).detail;
      if (cmd) setSlashPopover({ cmd, x, y });
    };
    window.addEventListener('show-slash-options', onShow);
    return () => window.removeEventListener('show-slash-options', onShow);
  }, []);

  // Explain routing modal — opened by QuotePill "Explain" action
  useEffect(() => {
    const onExplainRequest = (e: Event) => {
      const { text, sourceEl } = (e as CustomEvent<ExplainPayload>).detail;
      if (text) setExplainPayload({ text, sourceEl });
    };
    document.addEventListener('explain-routing-request', onExplainRequest);
    return () => document.removeEventListener('explain-routing-request', onExplainRequest);
  }, []);

  // Plan review: prefill compose input with review prompt so user can add context before sending
  useEffect(() => {
    const onReview = async (e: Event) => {
      const { planId, planName, projectKey } = (e as CustomEvent<{ planId: string; planName: string; projectKey: string }>).detail;
      if (!planId) return;

      // Reuse existing review session for this plan if one exists
      const existing = sessions.find((s) => s.linkedPlanId === planId);
      if (existing) {
        openInTab(existing.id);
        goToChat();
        // Prefill with a follow-up prompt so user can add context
        setTimeout(() => {
          if (currentSessionIdRef.current) {
            useComposerStore.getState().appendOrReplaceDraft(
              currentSessionIdRef.current,
              `Continuing review of plan **"${planName}"** (project: ${projectKey}).\n\n`,
              'replace',
            );
          }
          document.dispatchEvent(new CustomEvent('focus-composer'));
        }, 150);
        return;
      }

      // No existing review session — create one but prefill instead of auto-sending
      try {
        const { authFetch } = await import('../api/authFetch');
        const res = await authFetch(`/api/plans/${planId}/content`);
        if (!res.ok) return;
        const { content } = await res.json();
        const prompt = [
          `You are reviewing the implementation plan **"${planName}"** (project: ${projectKey}).`,
          `Please analyze this plan: identify what is still relevant, what may have drifted from current code,`,
          `what tasks are already completed, and what should be updated or archived.`,
          `\n\n---\n\n${content}\n\n---\n\n`,
        ].join(' ');
        newSession(opusModelId, `Review: ${planName}`, planId);
        clearAllTags();
        goToChat();
        // Prefill after session switch settles — user can edit/append before sending
        setTimeout(() => {
          if (currentSessionIdRef.current) {
            useComposerStore.getState().appendOrReplaceDraft(currentSessionIdRef.current, prompt, 'replace');
          }
          document.dispatchEvent(new CustomEvent('focus-composer'));
        }, 150);
      } catch { /* ignore */ }
    };
    window.addEventListener('valko-review-plan', onReview);
    return () => window.removeEventListener('valko-review-plan', onReview);
  }, [sessions, newSession, openInTab, clearAllTags, goToChat]);

  // Open existing session from plan review link
  useEffect(() => {
    const onOpen = (e: Event) => {
      const { sessionId } = (e as CustomEvent<{ sessionId: string }>).detail;
      if (sessionId) {
        openInTab(sessionId);
        goToChat(sessionId);
      }
    };
    window.addEventListener('valko-open-session', onOpen);
    return () => window.removeEventListener('valko-open-session', onOpen);
  }, [openInTab, goToChat]);

  // Navigate to source session from a saved post (↩ Source button in files drawer)
  useEffect(() => {
    const onNavigate = (e: Event) => {
      const { sessionId, messageIds } = (e as CustomEvent<{ sessionId: string; messageIds: string[] }>).detail;
      if (!sessionId) return;
      const exists = sessions.some(s => s.id === sessionId);
      if (!exists) {
        // Session was deleted — show brief visual feedback via a toast-like CustomEvent
        window.dispatchEvent(new CustomEvent('va:toast', { detail: { message: 'Source session was deleted', type: 'warn' } }));
        return;
      }
      openInTab(sessionId);
      goToChat(sessionId);
      if (messageIds?.length) {
        // Scroll to the first source message after a short delay for the session to load
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('va:scroll-to-message', { detail: { messageId: messageIds[0] } }));
        }, 400);
      }
    };
    window.addEventListener('navigate-to-source-post', onNavigate);
    return () => window.removeEventListener('navigate-to-source-post', onNavigate);
  }, [sessions, openInTab, goToChat]);

  // Roadmap: generate investor presentation via Opus
  useEffect(() => {
    const onGenerate = (e: Event) => {
      const { projectKey, roadmapPath } = (e as CustomEvent<{ projectKey: string; roadmapPath: string | null }>).detail;
      if (!projectKey) return;
      const prompt = [
        `You are generating a professional investor-ready presentation deck for the **${projectKey}** project.`,
        roadmapPath ? `The roadmap is at \`${roadmapPath}\` — read it first for strategic context.` : '',
        `Create a rich, visually striking HTML presentation using the /presentation skill.`,
        `Emphasize: product vision, key milestones, traction, business model, and next steps.`,
        `Save the output to \`${projectKey}/_operational_docs/presentations/\`.`,
      ].filter(Boolean).join(' ');
      setPendingPrompt(prompt);
      newSession(opusModelId, `Deck: ${projectKey}`);
      clearAllTags();
      goToChat();
    };
    window.addEventListener('valko-generate-roadmap-presentation', onGenerate);
    return () => window.removeEventListener('valko-generate-roadmap-presentation', onGenerate);
  }, [newSession, setPendingPrompt, clearAllTags, goToChat]);

  // valko-share: unified share event from any surface (file drawer, plans, roads, etc.)
  useEffect(() => {
    const onShare = (e: Event) => {
      const payload = (e as CustomEvent).detail as SharePayload;
      if (payload) doSharePayload(payload);
    };
    window.addEventListener('valko-share', onShare);
    return () => window.removeEventListener('valko-share', onShare);
  }, [doSharePayload]);

  // Ctrl/Cmd+F → open chat find bar
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        // Only hijack if we're on the chat panel (not in an input/textarea already)
        const active = document.activeElement;
        const isTyping = active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;
        if (!isTyping) {
          e.preventDefault();
          chatFind.openFind();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [chatFind.openFind]);

  // Show main UI controls (drawers, context bar, toggles) when not in SSH mode,
  // or when SSH'd to the same machine (isSelf) since the user is still operating the server.
  const showMainUI = !sshConnected || !!sshTarget?.isSelf;
  /** Context + / + projects docked above text input (thumb-friendly, right). SSH shell mode uses floating context-only stack. */
  const thumbDockActive = !sshConnected || (!!sshTarget?.isSelf && consoleView);

  // Extract tappable URLs from the last 60 lines of SSH output for the link bar.
  const detectedSshUrls = useMemo(() => {
    if (!sshConnected) return [];
    const text = stripAnsi(sshOutput.slice(-60).join(''));
    // Reconstruct hard-wrapped URLs. A naive line-bounded regex truncates a long URL
    // (e.g. the `claude login` OAuth URL printed across PTY-width lines) at the first
    // wrap — ".../authorize?code=" — which makes the tap-to-open chip useless on mobile.
    const matches = reconstructWrappedUrls(text);
    if (matches.length === 0) return [];
    return matches.slice(-5); // at most 5 most-recent unique URLs
  }, [sshOutput, sshConnected]);

  // Phase 4: toggle is a one-liner that flips the store; the TTS-stop side
  // Phase 4: toggleAutoSpeak callback deleted — SettingsModal + UnifiedDrawer
  // own their own toggle handlers now (each calls patchMemo directly).
  // Stop in-flight TTS whenever auto-speak flips off — reactive so it fires
  // regardless of which UI surface initiated the change (SettingsModal Discard,
  // future cross-tab sync). Was previously inline in the now-deleted handler.
  useEffect(() => {
    if (!autoSpeak) stop();
  }, [autoSpeak, stop]);

  const toggleConsoleView = useCallback(() => {
    setConsoleView((prev) => {
      const next = !prev;
      try { localStorage.setItem('voice-assistant-console', String(next)); } catch {}
      return next;
    });
  }, []);

  // Phase 4: handleToggleAutoSubmit deleted — SettingsModal owns it.

  useEffect(() => {
    // Always subscribe to auth changes — even on /auth page, so that
    // after GSI login completes we detect the new auth state and redirect.
    const unsub = onAuthChange((user) => {
      const isAuth = !!user;
      setAuthenticated(isAuth);
      if (user) setUserInitial((user.name || user.email || '?')[0].toUpperCase());
      setLoading(false);
      if (!isAuth) {
        if (!window.location.pathname.startsWith('/auth')) {
          window.location.href = '/auth';
        }
        return;
      }
      // Authenticated — redirect away from auth page
      if (window.location.pathname.startsWith('/auth')) {
        window.location.href = '/';
        return;
      }
      // Load profile feature flags
      getProfile().then((p) => {
        // voice_only_mode brand guard (defense in depth) — Valko hides every
        // code/terminal/ops surface regardless of backend feature flags.
        // Backend gates remain authoritative for security; this is purely
        // belt-and-braces in case a flag is misconfigured.
        const voiceOnly = getBrand().voiceOnlyMode;
        setMemosEnabled(voiceOnly ? false : p.features.memos_enabled);
        setBillingEnabled(!!p.features.billing_enabled);
        if (p.features.max_open_tabs) setMaxOpenTabs(p.features.max_open_tabs);
        // Phase 2: free_tier_single_session no longer mutates activeView (there is no
        // list view to leave); the free-tier single-session cap is enforced via
        // maxOpenTabs + render-layer (see [[free-tier-view-coupling]]).
        setInterAgentSharingEnabled(p.features.inter_agent_sharing_enabled !== false);
        setContextualDrawerMigration(p.features.contextual_drawer_migration !== false);
        setSshTerminalEnabled(voiceOnly ? false : !!p.features.ssh_terminal_enabled);
        setSandboxFsEnabled(!!p.features.sandbox_fs_enabled);
        setOpsPanelEnabled(voiceOnly ? false : !!p.features.ops_panel_enabled);
        setDevPreviewEnabled(voiceOnly ? false : !!p.features.dev_preview_enabled);
        // Agent Runs (agent-runs-visualization.md §4). Voice-only brands hide the whole
        // dev surface. This flag only decides whether to RENDER the icon — the panel's
        // data is gated server-side (workflow_viz_enabled ∧ admin), which is the gate
        // that actually matters; a flipped client flag still gets a 403.
        setWorkflowVizEnabled(voiceOnly ? false : !!p.features.workflow_viz_enabled);
        setProjectChipsEnabled(voiceOnly ? false : !!p.features.project_chips_enabled);
        setLessonGeneratorEnabled(!!p.features.lesson_generator_enabled);
        setReaderModeEnabled(!!p.features.reader_mode_enabled);
        setLogbookEnabled(!!p.features.logbook_enabled);
        // GearMenu entry gates (default true — undefined means on, matching DEFAULT_PROFILE)
        setBrowserPanelEnabled(p.features.browser_panel_enabled !== false);
        setPulseEnabled(p.features.pulse_enabled !== false);
        // Feature-surface tabs (SE-PF4) — default true (undefined = on); Protofac sets roads off.
        // Dev tabs (Tasks/Plans/Roads) are additionally hidden wholesale on voice-only brands.
        setTasksTabEnabled(p.features.tasks_tab_enabled !== false);
        setPlansTabEnabled(p.features.plans_tab_enabled !== false);
        setRoadsTabEnabled(p.features.roads_tab_enabled !== false);
        setUsagePanelEnabled(p.features.usage_panel_enabled !== false);
        // Guide button reuses the existing guide_enabled flag that already gates the backend /api/guide endpoint
        setGuideEnabled(p.features.guide_enabled !== false);
        setModelSelectorEnabled(!!p.features.model_selector_enabled);
        // Gemini master switch — off only when the profile explicitly says false
        // (undefined defaults to on, matching the backend DEFAULT_PROFILE).
        setGeminiEnabled(p.features.gemini_enabled !== false);
        // Browser-native voice (Protofac): stash profile voice routing so useTTS narrates
        // via window.speechSynthesis (when 'browser') and Settings hides server-voice
        // controls. Transient (not persisted). SE-PF7.
        useSettingsStore.getState().setProfileVoice(
          p.features.tts_provider ?? 'piper',
          p.features.whisper_enabled !== false,
        );
        setProfileLoaded(true);
      }).catch(() => { setProfileLoaded(true); }); // fail-open: keep defaults on error
      // Load user preferences after auth. Phase 4: autoPlay + autoSubmitVoice +
      // sttLanguage are now sourced from useSettingsStore (populated by
      // loadFromBackend, fired in parallel below). The getSettings() call here
      // is retained for the non-store fields (consent, provisioningStatus,
      // preferWhisper, dir-name local mirrors, diagramStyle DOM write).
      void useSettingsStore.getState().loadFromBackend();
      getSettings().then((s) => {
        if (s.memoSettings.memoLanguage) {
          setSttLang(s.memoSettings.memoLanguage === 'bg' ? 'bg-BG' : 'en-US');
          // Mirror memo language → device-local sttLanguage in the store
          // (preserves the pre-Phase-4 coupling; future plan may decouple).
          useSettingsStore.getState().setSttLanguage(s.memoSettings.memoLanguage);
        }
        if (s.memoSettings.tempDocsDirName) setTempDocsDirName(s.memoSettings.tempDocsDirName);
        if (s.memoSettings.plansDirName) setPlansDirName(s.memoSettings.plansDirName);
        if (s.memoSettings.projectDocsDirName) setProjectDocsDirName(s.memoSettings.projectDocsDirName);
        if (s.memoSettings.registryDirName) setRegistryDirName(s.memoSettings.registryDirName);
        if (s.memoSettings.archiveDirName) setArchiveDirName(s.memoSettings.archiveDirName);
        // Diagram style — written to DOM so MarkdownContent can re-init Mermaid when it changes
        const ds = s.memoSettings.diagramStyle || 'auto';
        document.documentElement.setAttribute('data-diagram-style', ds);
        // Protofac geo/VAT gate (signup-gate Phase 4): show until the user has declared an allowed
        // signup country. Comes BEFORE consent in the flow (rendered above + consent gated behind it).
        if (getBrandId() === 'protofac' && !s.country) {
          setGeoNeeded(true);
        }
        // Show consent modal when: (1) ToS never accepted, (2) the accepted consent-bundle
        // version is stale (backend `upToDate === false` → re-gate after a ToS/Privacy bump),
        // or (3) Protofac brand and the credit-disclosure was never acknowledged.
        if (
          !s.consent?.tosAcceptedAt ||
          s.consent?.upToDate === false ||
          (getBrandId() === 'protofac' && !s.consent?.creditConsentedAt)
        ) {
          setConsentNeeded(true);
        }
        // Show onboarding wizard for users who haven't provisioned a sandbox yet.
        // Two triggers: (1) tier 'managed' (the original managed-tier path), OR
        // (2) the Protofac brand — every invited Protofac user needs a sandbox
        // regardless of tier (they sign up 'free'; gating on tier left them stuck and
        // dropped into the BYOM Host/Port form — #1060). Admins are excluded (authority
        // comes from role, not tier) and 'ready' users have already provisioned.
        if (s?.provisioningStatus) setUserProvisioningStatus(s.provisioningStatus);
        setPreferWhisper(s?.preferWhisper === true);
        if ((s?.tier === 'managed' || getBrandId() === 'protofac') && s?.provisioningStatus !== 'ready' && s?.role !== 'admin') {
          setOnboardingNeeded(true);
        }
        // Pass GitHub state to the onboarding wizard
        if (s?.github?.connected) {
          setOnboardingGithubConnected(true);
          setOnboardingGithubLogin(s.github.login);
        }
        if (s?.githubFeatures?.oauthEnabled) {
          setOnboardingGithubOauthEnabled(true);
        }
        // Handle GitHub OAuth redirect params
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('github_connected')) {
          // Open settings to show the newly connected GitHub account
          openSettings();
          window.history.replaceState({}, '', window.location.pathname);
        } else if (urlParams.has('github_error')) {
          console.warn('GitHub OAuth error:', urlParams.get('github_error'));
          window.history.replaceState({}, '', window.location.pathname);
        }
        // Handle Stripe billing redirect params
        const billingParam = urlParams.get('billing');
        if (billingParam === 'success') {
          openSettings();
          coachShow({ id: 'billing', kind: 'toast', body: 'Subscription activated! Your plan has been updated.', ttlMs: 5000 });
          window.history.replaceState({}, '', window.location.pathname);
        } else if (billingParam === 'cancel') {
          openSettings();
          coachShow({ id: 'billing', kind: 'toast', body: 'Checkout cancelled — no charge was made.', ttlMs: 4000 });
          window.history.replaceState({}, '', window.location.pathname);
        }
      }).catch(() => {});
    });
    return unsub;
  }, []);

  async function handleLogout() {
    setAuthenticated(false); // Hide session list immediately — don't wait for signOut async
    const { signOut } = await import('../auth/google-auth');
    await signOut();
    window.location.replace('/auth');
  }

  return {
    CONSULT_PROVIDERS,
    activeComposeExpanded,
    activeRole,
    activeRoleRef,
    activeView,
    attachError,
    attachProcessing,
    attachments,
    authenticated,
    autoSubmitVoice,
    billingEnabled,
    browserConsentPrompt,
    browserPanelEnabled,
    browserTierGatePrompt,
    busy,
    cancelPrompt,
    chatFind,
    chatSlideRef,
    chatTab,
    chatTtsAllowed,
    checkedProjects,
    clearAttachments,
    clearLogbookState,
    clearProviderError,
    closeDrawerH,
    closeFiles,
    closeLogbook,
    closeSettings,
    confirmDelete,
    confirmReset,
    connected,
    consentNeeded,
    consoleView,
    consultTarget,
    contextualDrawerMigration,
    currentSessionId,
    cycleSpeed,
    deleteDialogOpen,
    deleteSaveKnowledge,
    deleteSession,
    deleteTargetIds,
    deployPending,
    deployStatus,
    detectedSshUrls,
    devPreviewEnabled,
    workflowVizEnabled,
    dismissInstallPill,
    doShare,
    doShareExternal,
    doSharePayload,
    drawerCommands,
    drawerOpen,
    drawerPanel,
    drawerTab,
    emitOpusConsult,
    emitOpusDismissed,
    enterShareSelection,
    exitSelectionMode,
    explainPayload,
    fetchSessionHealth,
    fetchSshTargets,
    fileInputProps,
    formatMultiMessageMarkdown,
    geminiEnabled,
    geoNeeded,
    getCheckedKeys,
    goToChat,
    guideEnabled,
    handleActAs,
    handleContinueSession,
    handleExpandedChange,
    handleExplainHere,
    handleExplainToNewSession,
    handleExplainToSession,
    handleInstallPrompt,
    handleLogout,
    handleNewSession,
    handleReplayCoach,
    handleRunPlanHere,
    handleSelectSession,
    handleSendToSession,
    handleShareAsPdf,
    handleShareFileExternal,
    handleShareFilePdf,
    handleShareMedia,
    handleShareSingle,
    handleShareToNewSession,
    handleSpeakText,
    handleThemeSwitch,
    handleWorkOnItem,
    hasAttachments,
    idleSet,
    installDismissed,
    installPromptAvailable,
    interAgentSharingEnabled,
    isAdmin,
    isAdminContext,
    isSessionSoundOn,
    kebabOpen,
    kebabRef,
    keyboardStripOpen,
    lastDeploy,
    leftShadowRef,
    leftSliverRef,
    lessonGeneratorEnabled,
    loadOlderMessages,
    loading,
    logbookEnabled,
    logbookOpen,
    logbookScreenshot,
    logbookText,
    memosEnabled,
    messages,
    modelSelectorEnabled,
    modelSheetOpen,
    onboardingGithubConnected,
    onboardingGithubLogin,
    onboardingGithubOauthEnabled,
    onboardingNeeded,
    openBrowser,
    openDeleteDialog,
    openDrawerH,
    openFiles,
    openInTab,
    openListDeleteDialog,
    openLogbook,
    openResetDialog,
    openSettings,
    openTabs,
    opsPanelEnabled,
    opusPrompt,
    opusSuggestionSource,
    pendingHighCostSend,
    pendingSharePayloadRef,
    pendingShareTargetRef,
    pendingShareTextRef,
    pinProject,
    pinSessionToList,
    plansDirName,
    plansTabEnabled,
    preferWhisper,
    previewVoice,
    profileLoaded,
    projectChipsEnabled,
    projectDocsDirName,
    providerErrors,
    providerInfo,
    pullDistance,
    pulling,
    pulseEnabled,
    readerModeEnabled,
    refreshing,
    registryDirName,
    registryModels,
    removeAttachment,
    renameOpen,
    renameSession,
    renameValue,
    requestSshSummary,
    resetDialogOpen,
    resetSaveKnowledge,
    rightShadowRef,
    rightSliverRef,
    roadsTabEnabled,
    sandboxFsEnabled,
    selectedMessageIds,
    selectedVoiceId,
    selectedVoiceName,
    selectionMode,
    selectionPurpose,
    sendAudioChunk,
    sendText,
    sendTextWithOverride,
    sendWithAttachments,
    sessionHealth,
    sessionMeta,
    sessions,
    setActiveRole,
    setBrowserConsentPrompt,
    setBrowserTierGatePrompt,
    setChatAtBottom,
    setChatPanelEl,
    setChatTab,
    setConsentNeeded,
    setDeleteDialogOpen,
    setDeleteSaveKnowledge,
    setDeleteTargetIds,
    setDrawerOpen,
    setDrawerTab,
    setExplainPayload,
    setGeoNeeded,
    setKebabOpen,
    setKeyboardStripOpen,
    setLogbookScreenshot,
    setLogbookText,
    setModelSheetOpen,
    setOnboardingNeeded,
    setOpusPrompt,
    setPendingHighCostSend,
    setRenameOpen,
    setRenameValue,
    setResetDialogOpen,
    setResetSaveKnowledge,
    setShareTargetOpen,
    setSlashPopover,
    setSleepConfirm,
    setSshPickerOpen,
    setSttLang,
    setViewportFocus,
    setVoice,
    setVoicePickerOpen,
    sessionsSynced,
    shareTargetOpen,
    slashPopover,
    sleepConfirm,
    socketRef,
    speaking,
    speakingSourceKey,
    sshConnect,
    sshConnected,
    sshDisconnect,
    sshExitTerminal,
    sshOutput,
    sshOutputEpoch,
    sshPickerOpen,
    sshResize,
    sshSendInput,
    sshTarget,
    sshTargets,
    sshTermRef,
    sshTerminalEnabled,
    sshVoiceHistory,
    stop,
    sttLang,
    sttLanguage,
    summarizing,
    switchTabWithHistory,
    tags,
    tasksTabEnabled,
    tempDocsDirName,
    theme,
    toggleAutoDocsPaused,
    toggleMessageSelection,
    toggleProject,
    toggleSessionSound,
    toolActivities,
    inflightToolRowId,
    transcribeAudio,
    triggerFilePicker,
    ttsSpeed,
    unpinProject,
    unpinSessionFromList,
    updateAutoDocsThreshold,
    updateSessionTuning,
    usage,
    usagePanelEnabled,
    userAutoDocsThreshold,
    userEmail,
    userInitial,
    userProvisioningStatus,
    visibleDots,
    voicePickerOpen,
    voices,
    wasmActive
  };
}
